import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  Plus,
  Loader2,
  Edit,
  Building2,
  UserCog,
  Mail,
  Lock,
  Power,
  RotateCcw,
  Trash2,
  FileText,
  ChevronUp,
  ChevronDown,
  Wifi,
  WifiOff,
  RefreshCw,
  Bot,
  MessageSquare,
  Save,
  LogIn,
  Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const DEFAULT_RGPD_LEGAL_TEXT = `Consiento el tratamiento de mis datos personales por el centro con la finalidad de gestionar mis reservas, servicios y comunicación relacionada con la prestación del servicio.
He sido informado/a de que puedo ejercer mis derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad, así como retirar este consentimiento en cualquier momento.
Los datos se conservarán durante el tiempo necesario para la gestión del servicio y las obligaciones legales aplicables.`;

const FICHA_SECTION_KEYS = ['personalData', 'rgpd'];

const DEFAULT_FICHA_SECTION_KEYS = FICHA_SECTION_KEYS;

const normalizeFichaSections = (value) => {
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];

  const seen = new Set();

  return raw
    .map((item) => (item || '').trim())
    .filter((item) => DEFAULT_FICHA_SECTION_KEYS.includes(item))
    .filter((item) => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
};

const buildFichaSectionOrder = (orderValue, enabledValue = []) => {
  const merged = [
    ...normalizeFichaSections(orderValue),
    ...normalizeFichaSections(enabledValue),
    ...DEFAULT_FICHA_SECTION_KEYS,
  ];

  const seen = new Set();

  return merged.filter((item) => {
    if (!DEFAULT_FICHA_SECTION_KEYS.includes(item) || seen.has(item)) return false;
    seen.add(item);
    return true;
  });
};

const moveFichaSection = (sections, key, direction) => {
  const currentIndex = sections.indexOf(key);
  if (currentIndex === -1) return sections;

  const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= sections.length) return sections;

  const next = [...sections];
  [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
  return next;
};

const buildCenterFormData = (center = null, admin = null) => {
  const enabledSections = normalizeFichaSections(center?.clientFichaSections);
  const clientFichaSections = enabledSections.length
    ? enabledSections
    : [...DEFAULT_FICHA_SECTION_KEYS];

  return {
    name: center?.name || '',
    timezone: center?.timezone || 'Europe/Madrid',
    openingTime: center?.openingTime || '08:00',
    closingTime: center?.closingTime || '21:00',
    adminName: admin?.fullName || '',
    adminEmail: admin?.email || '',
    adminPassword: '',
    clientFichaSections,
    clientFichaOrder: buildFichaSectionOrder(clientFichaSections, clientFichaSections),
    rgpdLegalText: center?.rgpdLegalText || DEFAULT_RGPD_LEGAL_TEXT,
    noBilling: false,
  };
};

const WA_STATUS_CONFIG = {
  not_connected:        { color: 'text-zinc-500 bg-zinc-500/10',      dot: '#71717a' },
  pending_review:       { color: 'text-yellow-400 bg-yellow-400/10',   dot: '#facc15' },
  pending_registration: { color: 'text-orange-400 bg-orange-400/10',   dot: '#fb923c' },
  registering:          { color: 'text-blue-400 bg-blue-400/10',       dot: '#60a5fa' },
  active:               { color: 'text-green-400 bg-green-400/10',     dot: '#4ade80' },
  failed:               { color: 'text-red-400 bg-red-400/10',         dot: '#f87171' },
  rate_limited:         { color: 'text-purple-400 bg-purple-400/10',   dot: '#c084fc' },
};

export const canStartCenterClientExport = (centerId, exportingCenterId) =>
  Boolean(centerId) && !exportingCenterId;

export const exportCenterClients = async ({ api, centerId, windowRef = window, onError }) => {
  let objectUrl;
  try {
    const res = await api.get(
      `/admin/centers/${encodeURIComponent(centerId)}/clients/export.csv`,
      { responseType: 'blob' }
    );
    objectUrl = windowRef.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
    const link = windowRef.document.createElement('a');
    link.href = objectUrl;
    link.download = `adamsched-clientes-${String(centerId).replace(/[^a-zA-Z0-9_-]/g, '_')}.csv`;
    windowRef.document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    onError?.(err);
  } finally {
    if (objectUrl) windowRef.URL.revokeObjectURL(objectUrl);
  }
};

const WEBSITE_URL_RE = /^https?:\/\/[^\s]+$/i;
const WEBSITE_SLUG_RE = /^[a-z0-9-]{3,40}$/;

// Derive the default website slug from a centre (its booking slug if any,
// else a slugified name/id). This is only the DEFAULT shown/pre-used at
// creation; per D16 the website slug is independent of the booking slug and
// the operator can override it in the create form before POSTing.
const deriveWebsiteSlug = (center) =>
  center?.slug
  || String(center?.name || center?.id || '')
       .toLowerCase()
       .replace(/[^a-z0-9-]+/g, '-')
       .replace(/^-|-$/g, '')
       .slice(0, 40);
const WEBSITE_ALLOWED_HOSTS = ['app.adamsched.com', 'www.adamsched.com', 'adamsched.com'];
const BLOB_HOST_RE = /(^|\.)public\.blob\.vercel-storage\.com$/i;

const isAllowedWebsiteUrl = (value, { asset = false, legacy = false, anyHost = false } = {}) => {
  if (!value) return true;
  if (!WEBSITE_URL_RE.test(value) || /\b(?:javascript|data):/i.test(value)) return false;
  try {
    const url = new URL(value);
    if (asset) return url.protocol === 'https:' && BLOB_HOST_RE.test(url.hostname);
    if (legacy) return true;
    if (anyHost) return true;
    return WEBSITE_ALLOWED_HOSTS.includes(url.hostname.toLowerCase()) || BLOB_HOST_RE.test(url.hostname);
  } catch {
    return false;
  }
};

const buildCenterWebsiteDraft = (website = null) => ({
  slug: website?.slug || '',
  status: website?.status || 'DISABLED',
  meta: { title: website?.meta?.title || '', description: website?.meta?.description || '', ogImageUrl: website?.meta?.ogImageUrl || '' },
  brand: {
    primaryColor: website?.brand?.primaryColor || '#18181b',
    accentColor: website?.brand?.accentColor || '#f97316',
    logoUrl: website?.brand?.logoUrl || '',
  },
  content: {
    hero: {
      headline: website?.content?.hero?.headline || '',
      subheadline: website?.content?.hero?.subheadline || '',
      ctaLabel: website?.content?.hero?.ctaLabel || '',
      ctaUrl: website?.content?.hero?.ctaUrl || '',
    },
    sections: website?.content?.sections || [],
  },
  links: {
    bookingUrl: website?.links?.bookingUrl || '',
    clientLoginUrl: website?.links?.clientLoginUrl || '',
  },
  legal: {
    reuseExistingLogo: Boolean(website?.legal?.reuseExistingLogo),
    reuseExistingPhotos: Boolean(website?.legal?.reuseExistingPhotos),
    oldWebsite: website?.legal?.oldWebsite || '',
  },
  assets: { photos: Array.isArray(website?.assets?.photos) ? website.assets.photos : [] },
});

// QA-staging fix: the optional URL fields below are bound to controlled
// inputs that buildCenterWebsiteDraft seeds with '' — but the backend schema
// treats each as Optional[str] = None. None is accepted; '' falls through to
// validate_public_url and 422s ("public_url: must not be empty"), so saving a
// DISABLED draft with any blank URL was impossible. Per the schema/design
// contract an absent optional URL travels as null, not ''. Normalise blank ->
// null on the way out only. Non-empty values are forwarded untouched so they
// still hit the Blob allowlist / https validation server-side (this does NOT
// widen the allowlist, accept disallowed hosts, or invent defaults).
const _blankUrlToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);

const normalizeOptionalWebsiteUrls = (draft) => {
  if (!draft || typeof draft !== 'object') return draft;
  return {
    ...draft,
    meta: draft.meta ? { ...draft.meta, ogImageUrl: _blankUrlToNull(draft.meta.ogImageUrl) } : draft.meta,
    brand: draft.brand ? { ...draft.brand, logoUrl: _blankUrlToNull(draft.brand.logoUrl) } : draft.brand,
    content: draft.content
      ? {
          ...draft.content,
          hero: draft.content.hero
            ? { ...draft.content.hero, ctaUrl: _blankUrlToNull(draft.content.hero.ctaUrl) }
            : draft.content.hero,
        }
      : draft.content,
    links: draft.links
      ? {
          ...draft.links,
          bookingUrl: _blankUrlToNull(draft.links.bookingUrl),
          clientLoginUrl: _blankUrlToNull(draft.links.clientLoginUrl),
        }
      : draft.links,
    legal: draft.legal ? { ...draft.legal, oldWebsite: _blankUrlToNull(draft.legal.oldWebsite) } : draft.legal,
  };
};

const validateCenterWebsiteDraft = (draft) => {
  const missing = [];
  if (!WEBSITE_SLUG_RE.test(draft?.slug || '')) missing.push('slug');
  if (draft?.status === 'ACTIVE') {
    if (!draft?.meta?.title?.trim()) missing.push('meta.title');
    if (!draft?.meta?.description?.trim()) missing.push('meta.description');
    if (!draft?.content?.hero?.headline?.trim()) missing.push('content.hero.headline');
    if (!draft?.brand?.logoUrl) missing.push('brand.logoUrl');
    if (!Array.isArray(draft?.content?.sections) || draft.content.sections.length === 0) missing.push('content.sections');
  }
  for (const [field, value, options] of [
    ['brand.logoUrl', draft?.brand?.logoUrl, { asset: true }],
    ['meta.ogImageUrl', draft?.meta?.ogImageUrl, { asset: true }],
    ['links.bookingUrl', draft?.links?.bookingUrl, { anyHost: true }],
    ['links.clientLoginUrl', draft?.links?.clientLoginUrl, { anyHost: true }],
    ['legal.oldWebsite', draft?.legal?.oldWebsite, { legacy: true }],
  ]) if (value && !isAllowedWebsiteUrl(value, options)) missing.push(field);
  for (const [field, value] of Object.entries({
    'brand.primaryColor': draft?.brand?.primaryColor,
    'brand.accentColor': draft?.brand?.accentColor,
  })) if (!/^#[0-9a-f]{6}$/i.test(value || '')) missing.push(field);
  return [...new Set(missing)];
};

// Per-type content sections (D9). Section types are a discriminated union
// over `type`; each section is owned by its own UI editor (review
// finding ui-ux/high: replaces the previous raw-JSON textarea that
// silently swallowed parse errors with empty catch{}).
const SECTION_TYPE_OPTIONS = [
  { value: 'text',         label: 'Texto' },
  { value: 'services',     label: 'Servicios' },
  { value: 'schedule',     label: 'Horario' },
  { value: 'gallery',      label: 'Galería' },
  { value: 'pricing',      label: 'Precios' },
  { value: 'testimonials', label: 'Testimonios' },
  { value: 'location',     label: 'Ubicación' },
];

const createEmptySection = (type) => {
  switch (type) {
    case 'text':
      return { type: 'text', heading: '', body: '' };
    case 'services':
      return { type: 'services', items: [{ name: '', description: '' }] };
    case 'schedule':
      return { type: 'schedule', items: [{ day: '', hours: '' }] };
    case 'gallery':
      return { type: 'gallery', photoIds: [] };
    case 'pricing':
      return { type: 'pricing', items: [{ name: '', price: '', period: '' }] };
    case 'testimonials':
      return { type: 'testimonials', items: [{ quote: '', author: '' }] };
    case 'location':
      return { type: 'location', address: '', mapEmbed: '' };
    default:
      return { type: 'text', heading: '', body: '' };
  }
};

const formatCenterWebsiteError = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail?.missing && Array.isArray(detail.missing)) return detail.missing.join(', ');
  if (Array.isArray(detail)) return detail.map(item => item?.msg || item?.message || String(item)).join(' · ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return detail || 'No se pudo guardar la web pública';
};

export const Centers = () => {
  const { api, isSuperAdmin, switchToken, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [centers, setCenters] = useState([]);
  const [centerAdmins, setCenterAdmins] = useState({});
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCenter, setEditingCenter] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [createAdmin, setCreateAdmin] = useState(true);
  const [formData, setFormData] = useState(buildCenterFormData());

  // AI Secretary state
  const [aiSecStats, setAiSecStats] = useState({});
  const [showAddMinutesModal, setShowAddMinutesModal] = useState(false);
  const [addMinutesCenter, setAddMinutesCenter] = useState(null);
  const [addMinutesValue, setAddMinutesValue] = useState('');
  const [addMinutesLoading, setAddMinutesLoading] = useState(false);
  const [assistantIdDraft, setAssistantIdDraft] = useState({});
  const [phoneNumberIdDraft, setPhoneNumberIdDraft] = useState({});
  const [assistantIdSaving, setAssistantIdSaving] = useState({});

  // Collapsed state — all collapsed by default
  const [expandedCenters, setExpandedCenters] = useState({});
  const toggleExpanded = (centerId) =>
    setExpandedCenters(prev => ({ ...prev, [centerId]: !prev[centerId] }));

  // Subscriptions + per-center inline form state
  const [subscriptions, setSubscriptions] = useState({});
  const [centerForms, setCenterForms] = useState({});
  const [centerFormSaving, setCenterFormSaving] = useState({});
  const [centerWebsites, setCenterWebsites] = useState({});
  const [centerWebsiteSaving, setCenterWebsiteSaving] = useState({});
  const [centerWebsiteDrafts, setCenterWebsiteDrafts] = useState({});
  const [centerWebsiteErrors, setCenterWebsiteErrors] = useState({});
  const [centerWebsiteUploadLoading, setCenterWebsiteUploadLoading] = useState({});
  const websiteFileInputs = useRef({});

  // WhatsApp reminder credits modal
  const [showWaCreditsModal, setShowWaCreditsModal] = useState(false);
  const [waCreditsCenter, setWaCreditsCenter] = useState(null);
  const [waCreditsValue, setWaCreditsValue] = useState('');
  const [waCreditsLoading, setWaCreditsLoading] = useState(false);

  // Offboard (baja completa) confirmation modal
  const [showOffboardDialog, setShowOffboardDialog] = useState(false);
  const [offboardCenter, setOffboardCenter] = useState(null);
  const [offboardConfirmText, setOffboardConfirmText] = useState('');
  const [offboardLoading, setOffboardLoading] = useState(false);

  const [impersonating, setImpersonating] = useState({});
  const [exportingCenterId, setExportingCenterId] = useState(null);

  const handleExportClients = async (e, centerId) => {
    e.stopPropagation();
    if (!canStartCenterClientExport(centerId, exportingCenterId)) return;
    setExportingCenterId(centerId);
    try {
      await exportCenterClients({
        api,
        centerId,
        onError: (err) => toast.error(err?.response?.data?.detail || t('centers.toast.exportClientsError')),
      });
    } finally {
      setExportingCenterId(null);
    }
  };

  const handleImpersonate = async (e, center) => {
    e.stopPropagation();
    setImpersonating(prev => ({ ...prev, [center.id]: true }));
    try {
      const res = await api.post(`/superadmin/impersonate/${center.id}`);
      await switchToken(res.data.token, center.name);
      navigate('/dashboard');
    } catch (err) {
      // switchToken rejects on cache-purge failure; AuthContext has
      // already invalidated local credentials and activated
      // cacheSecurityError. Surface the error and route the user
      // toward /login, where the blocking page will render.
      toast.error(err?.message || 'Error al entrar como admin');
      navigate('/login');
    } finally {
      setImpersonating(prev => ({ ...prev, [center.id]: false }));
    }
  };

  // WhatsApp provision modal
  const [showWaDialog, setShowWaDialog] = useState(false);
  const [waCenter, setWaCenter] = useState(null);
  const [waFormData, setWaFormData] = useState({
    accessToken: '', wabaId: '', phoneNumberId: '', pin: '', displayName: '',
  });
  const [waFormLoading, setWaFormLoading] = useState(false);
  const [waRefreshing, setWaRefreshing] = useState({});
  const [waEmbeddedLoading, setWaEmbeddedLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const fetchSubscriptions = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/admin/subscriptions');
      const map = {};
      (res.data || []).forEach(s => { map[s.centerId] = s; });
      setSubscriptions(map);
    } catch {}
  }, [api, isSuperAdmin]);

  const fetchAiSecStats = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/admin/ai-secretary/stats');
      const map = {};
      const drafts = {};
      const phoneDrafts = {};
      (res.data || []).forEach(s => {
        map[s.centerId] = s;
        drafts[s.centerId] = s.assistantId || '';
        phoneDrafts[s.centerId] = s.phoneNumberId || '';
      });
      setAiSecStats(map);
      setAssistantIdDraft(prev => ({ ...drafts, ...prev }));
      setPhoneNumberIdDraft(prev => ({ ...phoneDrafts, ...prev }));
    } catch {
      // ignore
    }
  }, [api, isSuperAdmin]);

  const fetchCenters = useCallback(async () => {
    try {
      setLoading(true);
      const [centersRes, usersRes] = await Promise.all([
        api.get('/centers?include_inactive=true'),
        api.get('/users?role=CENTER_ADMIN'),
      ]);
      setCenters(centersRes.data);

      if (isSuperAdmin) {
        let websiteEntries = [];
        try {
          const response = await api.get('/admin/center-websites');
          const websitesByCenterId = Object.fromEntries(
            (response.data || []).map(website => [website.centerId, website])
          );
          websiteEntries = centersRes.data.map(center => [center.id, websitesByCenterId[center.id] || null]);
        } catch {
          websiteEntries = centersRes.data.map(center => [center.id, null]);
        }
        setCenterWebsites(Object.fromEntries(websiteEntries));
        setCenterWebsiteDrafts(Object.fromEntries(
          websiteEntries.map(([id, website]) => [id, buildCenterWebsiteDraft(website)])
        ));
      }

      // Init per-center form drafts
      const forms = {};
      centersRes.data.forEach(c => {
        const enabled = normalizeFichaSections(c.clientFichaSections);
        const sections = enabled.length ? enabled : [...DEFAULT_FICHA_SECTION_KEYS];
        forms[c.id] = {
          clientFichaSections: sections,
          clientFichaOrder: buildFichaSectionOrder(sections, sections),
          rgpdLegalText: c.rgpdLegalText || DEFAULT_RGPD_LEGAL_TEXT,
          fiscalName: c.fiscalName || '',
          fiscalId: c.fiscalId || '',
          billingEmail: c.billingEmail || '',
        };
      });
      setCenterForms(forms);

      // Map center admins by centerId
      const adminsMap = {};
      usersRes.data.forEach(admin => {
        if (admin.centerId) {
          adminsMap[admin.centerId] = admin;
        }
      });
      setCenterAdmins(adminsMap);
      fetchAiSecStats();
    } catch (error) {
      console.error('Failed to fetch centers:', error);
      toast.error(t('centers.toast.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isSuperAdmin]);

  const createCenterWebsite = async (center) => {
    if (!isSuperAdmin || !center?.id) return;
    // Re-review finding (ui-ux/MEDIUM, §9.11): the website slug is the
    // contractual public URL (www.adamsched.com/<slug>, R2) and is
    // independent of the booking slug (D16). Prefer the slug the operator
    // typed in the create form (draft.slug) so they can review/override it
    // in one step; fall back to the centre-derived default only when the
    // field is left blank. Validate the resulting candidate against
    // WEBSITE_SLUG_RE BEFORE POSTing so a sub-3-char / punctuation-only
    // value surfaces a precise client-side error instead of an opaque 422.
    const draft = centerWebsiteDrafts[center.id] || buildCenterWebsiteDraft(null);
    const enteredSlug = (draft.slug || '').trim();
    const candidateSlug = enteredSlug || deriveWebsiteSlug(center);
    if (!WEBSITE_SLUG_RE.test(candidateSlug)) {
      toast.error(`El slug "${candidateSlug}" no cumple el formato (3-40 caracteres, a-z, 0-9, guion). Corrige el slug antes de crear la web.`);
      return;
    }
    // F1 fix (review round 1): `CenterWebsiteCreate` requires `meta`
    // (title + description) and `content` (hero.headline) with NO
    // defaults — a bare {slug, status} body 422s every time. D15's
    // documented minimum create payload is slug + meta.title +
    // meta.description; the schema additionally requires
    // content.hero.headline (non-optional), so we collect that too via
    // the "Crear configuración inicial" inputs (bound through the same
    // centerWebsiteDrafts/updateCenterWebsiteDraft mechanism the full
    // editor uses) and block the POST client-side with the same
    // pattern saveCenterWebsite/activateCenterWebsite already use,
    // rather than letting the operator hit an opaque 422.
    const title = (draft.meta?.title || '').trim();
    const description = (draft.meta?.description || '').trim();
    const headline = (draft.content?.hero?.headline || '').trim();
    const missingCreateFields = [];
    if (!title) missingCreateFields.push('meta.title');
    if (!description) missingCreateFields.push('meta.description');
    if (!headline) missingCreateFields.push('content.hero.headline');
    if (missingCreateFields.length) {
      setCenterWebsiteErrors(prev => ({
        ...prev,
        [center.id]: `Campos obligatorios para crear: ${missingCreateFields.join(', ')}`,
      }));
      return;
    }
    setCenterWebsiteErrors(prev => ({ ...prev, [center.id]: '' }));
    setCenterWebsiteSaving(prev => ({ ...prev, [center.id]: true }));
    try {
      // D16 — centerId is the path parameter; the body cannot carry
      // centerId (Pydantic extra="forbid" would 422 it). Path-sourced
      // centerId is tenant-isolated by construction.
      const response = await api.post(`/admin/center-websites/centers/${center.id}`, {
        slug: candidateSlug,
        status: 'DISABLED',
        meta: { title, description },
        content: { hero: { headline } },
      });
      setCenterWebsites(prev => ({ ...prev, [center.id]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [center.id]: buildCenterWebsiteDraft(response.data) }));
      toast.success(t('success.centers.websiteCreated'));
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [center.id]: formatCenterWebsiteError(error) }));
      toast.error(formatCenterWebsiteError(error));
    } finally {
      setCenterWebsiteSaving(prev => ({ ...prev, [center.id]: false }));
    }
  };

  const updateCenterWebsiteDraft = (centerId, path, value) => {
    setCenterWebsiteDrafts(prev => {
      const next = { ...buildCenterWebsiteDraft(prev[centerId]) };
      const keys = path.split('.');
      let cursor = next;
      keys.slice(0, -1).forEach(key => {
        cursor[key] = { ...cursor[key] };
        cursor = cursor[key];
      });
      cursor[keys[keys.length - 1]] = value;
      return { ...prev, [centerId]: next };
    });
  };

  // Per-type content section helpers (D9). Each helper funnels through
  // setCenterWebsiteDrafts so the rest of the editor (PUT, validation,
  // error rendering) sees a uniform draft state.
  const updateContentSection = (centerId, index, updated) => {
    setCenterWebsiteDrafts(prev => {
      const cur = prev[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
      const sections = [...(cur.content?.sections || [])];
      sections[index] = updated;
      return { ...prev, [centerId]: { ...cur, content: { ...cur.content, sections } } };
    });
  };

  const addContentSection = (centerId, type) => {
    setCenterWebsiteDrafts(prev => {
      const cur = prev[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
      const sections = [...(cur.content?.sections || []), createEmptySection(type)];
      return { ...prev, [centerId]: { ...cur, content: { ...cur.content, sections } } };
    });
  };

  const removeContentSection = (centerId, index) => {
    setCenterWebsiteDrafts(prev => {
      const cur = prev[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
      const sections = (cur.content?.sections || []).filter((_, i) => i !== index);
      return { ...prev, [centerId]: { ...cur, content: { ...cur.content, sections } } };
    });
  };

  // Re-review finding (ui-ux/MEDIUM, §9.12): assets.photos[].alt is a
  // required D9 field that round-trips through the PUT body, but had no
  // editor surface — the operator could never author/correct the alt text a
  // photo carries on the public page (only the server-derived default set at
  // upload was ever shown, and only as a caption). Edit it per-photo through
  // the same draft mechanism; saveCenterWebsite already sends assets.photos.
  const updateCenterWebsitePhotoAlt = (centerId, photoId, alt) => {
    setCenterWebsiteDrafts(prev => {
      const cur = prev[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
      const photos = (cur.assets?.photos || []).map(p => (p.id === photoId ? { ...p, alt } : p));
      return { ...prev, [centerId]: { ...cur, assets: { ...cur.assets, photos } } };
    });
  };

  // Per-item editor for sections of type services / schedule / pricing /
  // testimonials. Keeping this in-module keeps the diff focused on the
  // discriminated union editor and avoids creating a new sibling file.
  const renderSectionItems = (section, idx, fields, centerId) => {
    const items = Array.isArray(section.items) ? section.items : [];
    const updateItem = (itemIdx, field, value) => {
      const next = items.map((item, i) => (i === itemIdx ? { ...item, [field]: value } : item));
      updateContentSection(centerId, idx, { ...section, items: next });
    };
    const addItem = () => {
      const next = [...items, fields.reduce((acc, [k]) => ({ ...acc, [k]: '' }), {})];
      updateContentSection(centerId, idx, { ...section, items: next });
    };
    const removeItem = (itemIdx) => {
      const next = items.filter((_, i) => i !== itemIdx);
      updateContentSection(centerId, idx, { ...section, items: next });
    };
    return (
      <div className="space-y-2">
        {items.map((item, itemIdx) => (
          <div key={itemIdx} className="border border-zinc-800 rounded p-2 space-y-1 bg-zinc-900/40">
            {fields.map(([field, label]) => (
              <Input
                key={field}
                value={item[field] || ''}
                onChange={e => updateItem(itemIdx, field, e.target.value)}
                placeholder={label}
                className="input-tactical text-xs h-8"
                data-testid={`website-section-${section.type}-item-${field}-${idx}-${itemIdx}-${centerId}`}
              />
            ))}
            <Button type="button" size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => removeItem(itemIdx)} data-testid={`website-section-${section.type}-item-remove-${idx}-${itemIdx}-${centerId}`}>
              Eliminar
            </Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" className="text-xs" onClick={addItem} data-testid={`website-section-${section.type}-item-add-${idx}-${centerId}`}>
          + Añadir
        </Button>
      </div>
    );
  };

  const saveCenterWebsite = async (centerId) => {
    const draft = centerWebsiteDrafts[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
    const missing = validateCenterWebsiteDraft(draft);
    // The activation gate (§5.3 / D15) only applies when the operator
    // intends to GO LIVE. Saving a DISABLED draft must be permissive — a
    // slug that is still being tuned, a default color hex, or a placeholder
    // section list is acceptable while the operator works iteratively. The
    // previous code branched on `draft.status !== 'ACTIVE'` and returned
    // early on the same `missing` list, which left the operator unable to
    // save partial progress. Backend validation surfaces real issues via
    // 422 once the operator tries to activate.
    if (draft.status === 'ACTIVE' && missing.length) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: `Campos obligatorios: ${missing.join(', ')}` }));
      return;
    }
    setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: '' }));
    setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: true }));
    try {
      const resourceSlug = centerWebsites[centerId]?.slug || draft.slug;
      const response = await api.put(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`, normalizeOptionalWebsiteUrls(draft));
      setCenterWebsites(prev => ({ ...prev, [centerId]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: buildCenterWebsiteDraft(response.data) }));
      toast.success(t('success.centers.websiteSaved'));
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: formatCenterWebsiteError(error) }));
    } finally {
      setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: false }));
    }
  };

  const activateCenterWebsite = async (centerId) => {
    const current = centerWebsiteDrafts[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
    const draft = { ...current, status: 'ACTIVE' };
    // Activation gate (D15 §5.3): status=ACTIVE requires the operator to have
    // filled in the required fields client-side; if anything is missing we must
    // BLOCK before issuing the PUT, the same way saveCenterWebsite does.
    // Re-review finding (ui-ux/MEDIUM): the previous order — mutate
    // centerWebsiteDrafts to status='ACTIVE' FIRST, then validate —
    // contaminated the operator's DISABLED draft on a failed activation.
    // The persisted in-memory draft was 'ACTIVE' with missing required
    // fields, so a subsequent saveCenterWebsite click hit the §9.7 gate
    // on the now-ACTIVE draft (instead of passing through to the
    // backend) and there is no UI affordance to revert status (it is
    // rendered read-only). Validate against a local draft FIRST; only
    // mutate state once validation passes.
    const missing = validateCenterWebsiteDraft(draft);
    if (missing.length) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: `Campos obligatorios: ${missing.join(', ')}` }));
      return;
    }
    // Validation passed: commit the draft to state and start saving.
    setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: draft }));
    setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: '' }));
    setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: true }));
    try {
      const resourceSlug = centerWebsites[centerId]?.slug || draft.slug;
      const response = await api.put(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`, normalizeOptionalWebsiteUrls(draft));
      setCenterWebsites(prev => ({ ...prev, [centerId]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: buildCenterWebsiteDraft(response.data) }));
      toast.success(t('success.centers.websiteActivated'));
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: formatCenterWebsiteError(error) }));
    } finally {
      setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: false }));
    }
  };

  // Re-review finding (ui-ux/MEDIUM, §9.13): the tab is the sole documented
  // lifecycle surface for the site (D15), and deactivation is a first-class
  // reversible operation (D8, DELETE /api/admin/center-websites/{slug}), but
  // there was no UI path to take an ACTIVE site down — the operator would
  // have to call the API by hand. Add a confirmed "Desactivar" action that
  // soft-disables the site (assets retained) and refreshes state from the
  // server, consistent with the §9.10 destructive-action confirmation.
  const deactivateCenterWebsite = async (centerId) => {
    if (!window.confirm('Se desactivará la web pública: dejará de servirse en www.adamsched.com. Los assets se conservan y podrás reactivarla más tarde. ¿Continuar?')) return;
    const website = centerWebsiteDrafts[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
    setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: '' }));
    setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: true }));
    try {
      const resourceSlug = centerWebsites[centerId]?.slug || website.slug;
      await api.delete(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`);
      const response = await api.get(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`);
      setCenterWebsites(prev => ({ ...prev, [centerId]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: buildCenterWebsiteDraft(response.data) }));
      toast.success(t('success.centers.websiteDeactivated'));
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: formatCenterWebsiteError(error) }));
    } finally {
      setCenterWebsiteSaving(prev => ({ ...prev, [centerId]: false }));
    }
  };

  const uploadCenterWebsiteAsset = async (centerId, kind, file, assetId = null) => {
    if (!file) return;
    const website = centerWebsiteDrafts[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
    setCenterWebsiteUploadLoading(prev => ({ ...prev, [`${centerId}-${kind}`]: true }));
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('kind', kind);
      if (assetId) body.append('assetId', assetId);
      const resourceSlug = centerWebsites[centerId]?.slug || website.slug;
      await api.post(`/admin/center-websites/${encodeURIComponent(resourceSlug)}/assets`, body);
      const response = await api.get(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`);
      setCenterWebsites(prev => ({ ...prev, [centerId]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: buildCenterWebsiteDraft(response.data) }));
      toast.success(kind === 'logo' ? 'Logo actualizado' : 'Foto subida');
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: formatCenterWebsiteError(error) }));
    } finally {
      setCenterWebsiteUploadLoading(prev => ({ ...prev, [`${centerId}-${kind}`]: false }));
    }
  };

  const deleteCenterWebsiteAsset = async (centerId, kind, assetId = null) => {
    // Re-review finding (ui-ux/HIGH, §9.10): deleting a logo or photo is an
    // irreversible Vercel Blob operation (D7) and, for the logo of an ACTIVE
    // site, can break the live public page (brand.logoUrl is required for
    // ACTIVE). Gate it behind a confirmation like every other destructive
    // action in this file (deactivateCenter, offboard, the §9.6 section-type
    // switch) so a single misclick cannot destroy a live asset.
    const confirmMsg = kind === 'logo'
      ? 'Se eliminará el logo de forma permanente del almacenamiento. Si la web está activa, la página pública puede quedar incompleta. ¿Continuar?'
      : 'Se eliminará esta foto de forma permanente del almacenamiento. ¿Continuar?';
    if (!window.confirm(confirmMsg)) return;
    const website = centerWebsiteDrafts[centerId] || buildCenterWebsiteDraft(centerWebsites[centerId]);
    try {
      const resourceSlug = centerWebsites[centerId]?.slug || website.slug;
      // F2 fix (review round 1): the backend endpoint declares `kind` /
      // `assetId` as `Form(...)` (backend/routers/center_websites.py
      // delete_asset), so it only binds from a form-encoded/multipart
      // body — a plain JSON `{data: {kind, assetId}}` (what axios.delete
      // sent previously) 422s every time. Send a FormData body, matching
      // the upload endpoint's client pattern above.
      const body = new FormData();
      body.append('kind', kind);
      if (assetId) body.append('assetId', assetId);
      await api.delete(`/admin/center-websites/${encodeURIComponent(resourceSlug)}/assets`, { data: body });
      const response = await api.get(`/admin/center-websites/${encodeURIComponent(resourceSlug)}`);
      setCenterWebsites(prev => ({ ...prev, [centerId]: response.data }));
      setCenterWebsiteDrafts(prev => ({ ...prev, [centerId]: buildCenterWebsiteDraft(response.data) }));
    } catch (error) {
      setCenterWebsiteErrors(prev => ({ ...prev, [centerId]: formatCenterWebsiteError(error) }));
    }
  };


  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    let out = '';
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  };

  const handleResetCenterAdminPassword = async (adminUser) => {
    if (!isSuperAdmin) return;
    if (!adminUser?.id) return;

    const label = adminUser?.fullName || adminUser?.name || adminUser?.email || t('centers.centerAdmin');
    const ok = window.confirm(t('centers.toast.confirmResetPassword', { label }));
    if (!ok) return;

    const newPass = generateTempPassword();
    try {
      await api.post(`/users/${adminUser.id}/set-password`, { password: newPass });
      if (typeof toast?.success === 'function') toast.success(t('centers.toast.passwordReset'));
      window.prompt(t('centers.toast.passwordReset'), newPass);
    } catch (e) {
      if (typeof toast?.error === 'function') toast.error(t('centers.toast.passwordResetError'));
      else window.alert(t('centers.toast.passwordResetError'));
    }
  };


const deactivateCenter = async (e, centerId) => {
  e.stopPropagation();
  if (!window.confirm(t('centers.toast.confirmDeactivate'))) return;
  try {
    await api.delete(`/centers/${centerId}`);
    toast.success(t('centers.toast.deactivated'));
    fetchCenters();
  } catch (error) {
    const msg = error?.response?.data?.detail || t('centers.toast.deactivateError');
      let safeMsg = msg;
      if (Array.isArray(safeMsg)) {
        safeMsg = safeMsg.map(e => e?.msg || e?.message || String(e)).join(' · ');
      } else if (safeMsg && typeof safeMsg === 'object') {
        safeMsg = JSON.stringify(safeMsg);
      }

    toast.error(safeMsg || 'Error');
  }
};

const toSectionsList = (value) => normalizeFichaSections(value);

const reactivateCenter = async (e, centerId) => {
  e.stopPropagation();
  try {
    await api.put(`/centers/${centerId}/reactivate`);
    toast.success(t('centers.toast.reactivated'));
    fetchCenters();
  } catch (error) {
    const msg = error?.response?.data?.detail || t('centers.toast.reactivateError');
    toast.error(msg);
  }
};

// El botón de la papelera invoca ahora la baja completa (offboard), no el antiguo
// hard-delete (DELETE /centers/{id}?hard=true). La confirmación es un modal con
// escritura del nombre exacto, no window.confirm.
const openOffboardDialog = (e, center) => {
  e.stopPropagation();
  setOffboardCenter(center);
  setOffboardConfirmText('');
  setShowOffboardDialog(true);
};

const handleOffboard = async () => {
  if (!offboardCenter) return;
  if (offboardConfirmText !== offboardCenter.name) return;
  setOffboardLoading(true);
  try {
    const res = await api.delete(`/superadmin/centers/${offboardCenter.id}/offboard`);
    const d = res?.data?.deleted || {};
    toast.success(
      `Centro eliminado — ${d.clients || 0} clientes · ${d.bookings || 0} reservas · ` +
      `${d.whatsapp_conversations || 0} conversaciones WhatsApp · ${d.users || 0} usuarios`
    );
    setShowOffboardDialog(false);
    fetchCenters();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 409) {
      // Guard fiscal (facturas emitidas): el detail del backend ya explica la
      // obligación de conservación de 5 años — lo mostramos tal cual.
      toast.error(error?.response?.data?.detail || 'No se puede eliminar: el centro tiene facturas emitidas.');
    } else if (status === 404) {
      toast.error(t('success.centers.notFound'));
    } else {
      let msg = error?.response?.data?.detail || 'Error al eliminar el centro';
      if (Array.isArray(msg)) {
        msg = msg.map(x => x?.msg || x?.message || String(x)).join(' · ');
      } else if (msg && typeof msg === 'object') {
        msg = JSON.stringify(msg);
      }
      toast.error(msg || 'Error');
    }
  } finally {
    setOffboardLoading(false);
  }
};


  useEffect(() => {
    fetchCenters();
    fetchSubscriptions();
  }, [fetchCenters, fetchSubscriptions]);

  const handleSaveCenterGeneral = async (centerId) => {
    const form = centerForms[centerId] || {};
    const center = centers.find(c => c.id === centerId);
    if (!center) return;
    setCenterFormSaving(prev => ({ ...prev, [centerId]: true }));
    try {
      await api.put(`/centers/${centerId}`, {
        name: center.name,
        timezone: center.timezone,
        active: center.active !== false,
        clientFichaSections: toSectionsList(form.clientFichaSections),
        rgpdLegalText: form.rgpdLegalText,
        fiscalName: form.fiscalName || null,
        fiscalId: form.fiscalId || null,
        billingEmail: form.billingEmail || null,
      });
      setCenters(cs => cs.map(c => c.id === centerId ? {
        ...c,
        clientFichaSections: toSectionsList(form.clientFichaSections),
        rgpdLegalText: form.rgpdLegalText,
        fiscalName: form.fiscalName || null,
        fiscalId: form.fiscalId || null,
        billingEmail: form.billingEmail || null,
      } : c));
      toast.success(t('success.centers.configSaved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setCenterFormSaving(prev => ({ ...prev, [centerId]: false }));
    }
  };

  const handleOpenDialog = (center = null) => {
    if (center) {
      setEditingCenter(center);
      const admin = centerAdmins[center.id];
      setFormData(buildCenterFormData(center, admin));
      setCreateAdmin(false);
    } else {
      setEditingCenter(null);
      setFormData(buildCenterFormData());
      setCreateAdmin(true);
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(t('centers.nameRequired') || 'Name is required');
      return;
    }

    // Validate admin fields for new center
    if (!editingCenter && createAdmin) {
      if (!formData.adminEmail.trim()) {
        toast.error(t('centers.adminEmailRequired') || 'Admin email is required');
        return;
      }
      if (!formData.adminPassword.trim() || formData.adminPassword.length < 6) {
        toast.error(t('centers.adminPasswordRequired') || 'Admin password is required (min 6 chars)');
        return;
      }
    }

    setFormLoading(true);
    try {
      if (editingCenter) {
        await api.put(`/centers/${editingCenter.id}`, {
          name: formData.name,
          timezone: formData.timezone,
          active: true,
          clientFichaSections: toSectionsList(formData.clientFichaSections),
          rgpdLegalText: formData.rgpdLegalText,
        });
        toast.success(t('centers.updated') || 'Center updated');
      } else {
        const payload = {
          name: formData.name,
          timezone: formData.timezone,
          active: true,
          clientFichaSections: toSectionsList(formData.clientFichaSections),
          rgpdLegalText: formData.rgpdLegalText,
          noBilling: formData.noBilling,
        };
        
        // Include admin data if creating admin
        if (createAdmin) {
          payload.adminName = formData.adminName || 'Center Admin';
          payload.adminEmail = formData.adminEmail;
          payload.adminPassword = formData.adminPassword;
        }
        
        await api.post('/centers', payload);
        toast.success(t('centers.created') || 'Center created with admin');
      }
      setShowDialog(false);
      fetchCenters();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save center';
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const openWaDialog = (center) => {
    setWaCenter(center);
    setWaFormData({
      accessToken: '',
      wabaId: center.whatsappWabaId || '',
      phoneNumberId: center.whatsappPhoneNumberId || '',
      pin: center.whatsappPin || '',
      displayName: center.whatsappDisplayName || '',
    });
    const notConnected = !center.whatsappStatus || center.whatsappStatus === 'not_connected';
    setShowGuide(notConnected);
    setShowWaDialog(true);
  };

  const handleWaProvision = async () => {
    if (!waFormData.wabaId || !waFormData.phoneNumberId || !waFormData.pin) {
      toast.error(t('centers.whatsapp.toast.provisionError'));
      return;
    }
    setWaFormLoading(true);
    try {
      const res = await api.post('/admin/whatsapp/provision', {
        centerId: waCenter.id,
        accessToken: waFormData.accessToken,
        wabaId: waFormData.wabaId,
        phoneNumberId: waFormData.phoneNumberId,
        pin: waFormData.pin,
        displayName: waFormData.displayName,
      });
      toast.success(`${t('centers.whatsapp.toast.provisionSuccess')} → ${res.data.status}`);
      setShowWaDialog(false);
      setCenters(cs => cs.map(c =>
        c.id === waCenter.id ? { ...c, whatsappStatus: res.data.status } : c
      ));
      fetchCenters();
    } catch (err) {
      const detail = err.response?.data?.detail || t('centers.whatsapp.toast.provisionError');
      toast.error(detail);
    } finally {
      setWaFormLoading(false);
    }
  };

  const handleEmbeddedSignup = (center) => {
    if (!/^\d{6}$/.test(waFormData.pin)) {
      toast.error(t('centers.whatsapp.toast.provisionError') + ' — ' + t('centers.whatsapp.provision.pin'));
      return;
    }

    if (!window.FB) {
      toast.error(t('centers.whatsapp.toast.sdkNotLoaded'));
      return;
    }

    // Mutable ref — populated by the postMessage handler before FB.login fires
    const signupData = { phoneNumberId: null, wabaId: null };

    const messageHandler = (event) => {
      if (event.origin !== 'https://www.facebook.com') return;
      let parsed;
      try { parsed = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; }
      catch { return; }
      if (parsed?.type === 'WA_EMBEDDED_SIGNUP' && parsed?.event === 'FINISH') {
        signupData.phoneNumberId = parsed.data?.phone_number_id || null;
        signupData.wabaId        = parsed.data?.waba_id || null;
      }
    };

    window.addEventListener('message', messageHandler);
    setWaEmbeddedLoading(true);

    window.FB.login(
      async (response) => {
        if (!response?.authResponse?.accessToken) {
          toast.info(t('centers.whatsapp.toast.signupCancelled'));
          window.removeEventListener('message', messageHandler);
          setWaEmbeddedLoading(false);
          return;
        }

        // Wait up to 3s for postMessage FINISH from Meta (race: FB.login callback
        // can fire before WA_EMBEDDED_SIGNUP postMessage arrives)
        if (!signupData.phoneNumberId || !signupData.wabaId) {
          await new Promise((resolve) => {
            let elapsed = 0;
            const interval = setInterval(() => {
              elapsed += 100;
              if (
                (signupData.phoneNumberId && signupData.wabaId) ||
                elapsed >= 3000
              ) {
                clearInterval(interval);
                resolve();
              }
            }, 100);
          });
        }

        if (!signupData.phoneNumberId || !signupData.wabaId) {
          toast.warning(t('centers.whatsapp.toast.noWabaData'));
          window.removeEventListener('message', messageHandler);
          setWaEmbeddedLoading(false);
          return;
        }

        const accessToken = response.authResponse.accessToken;
        const pin = waFormData.pin;
        const displayName = waFormData.displayName || '';
        setWaFormLoading(true);

        api.post('/admin/whatsapp/provision', {
          centerId: center.id,
          accessToken,
          wabaId: signupData.wabaId,
          phoneNumberId: signupData.phoneNumberId,
          pin,
          displayName,
        })
          .then((res) => {
            toast.success(`${t('centers.whatsapp.toast.provisionSuccess')} → ${res.data.status}`);
            setShowWaDialog(false);
            setCenters(cs => cs.map(c =>
              c.id === center.id ? { ...c, whatsappStatus: res.data.status } : c
            ));
            if (res.data.meta_error) {
              console.error('[WA provision] meta_error:', res.data.meta_error);
            }
          })
          .catch((err) => {
            const detail = err.response?.data?.detail || t('centers.whatsapp.toast.provisionError');
            toast.error(detail);
            if (err.response?.data?.meta_error) {
              console.error('[WA provision] meta_error:', err.response.data.meta_error);
            }
          })
          .finally(() => {
            window.removeEventListener('message', messageHandler);
            setWaEmbeddedLoading(false);
            setWaFormLoading(false);
          });
      },
      {
        scope: 'whatsapp_business_management,whatsapp_business_messaging',
        extras: {
          feature: 'whatsapp_embedded_signup',
          sessionInfoVersion: '3',
        },
      }
    );
  };

  const handleWaRefresh = async (centerId) => {
    setWaRefreshing(r => ({ ...r, [centerId]: true }));
    try {
      const res = await api.get(`/admin/whatsapp/status/${centerId}`);
      toast.success(`${t('centers.whatsapp.toast.statusRefreshed')} → ${res.data.status}`);
      setCenters(cs => cs.map(c =>
        c.id === centerId ? { ...c, whatsappStatus: res.data.status } : c
      ));
      if (res.data.status === 'active') {
        setShowGuide(false);
      }
    } catch (err) {
      toast.error(t('centers.whatsapp.toast.statusError'));
    } finally {
      setWaRefreshing(r => ({ ...r, [centerId]: false }));
    }
  };

  const waStatusKey = (status) => {
    const map = {
      not_connected: 'notConnected',
      pending_review: 'pendingReview',
      pending_registration: 'pendingRegistration',
      registering: 'registering',
      active: 'active',
      failed: 'failed',
      rate_limited: 'rateLimited',
    };
    return map[status] || 'notConnected';
  };

  const handleDailyPlannerEmailToggle = async (centerId, enabled) => {
    try {
      await api.put(`/admin/centers/${centerId}/daily-planner-email`, { enabled });
      setCenters(cs => cs.map(c => c.id === centerId ? { ...c, dailyPlannerEmailEnabled: enabled } : c));
      toast.success(enabled ? 'Resumen diario activado' : 'Resumen diario desactivado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleEmailRemindersToggle = async (centerId, enabled) => {
    try {
      await api.put(`/admin/centers/${centerId}/email-reminders`, { emailRemindersEnabled: enabled });
      setCenters(cs => cs.map(c => c.id === centerId ? { ...c, emailRemindersEnabled: enabled } : c));
      toast.success(enabled ? 'Recordatorios email activados' : 'Recordatorios email pausados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleAiSecPackageToggle = async (centerId, enabled) => {
    try {
      await api.put(`/admin/centers/${centerId}/ai-secretary-package`, { enabled });
      setCenters(cs => cs.map(c => c.id === centerId ? { ...c, aiSecretaryEnabled: enabled } : c));
      toast.success(enabled ? 'Paquete Secretaria IA activado' : 'Paquete Secretaria IA desactivado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleAiSecToggle = async (centerId, enabled) => {
    try {
      await api.put(`/admin/ai-secretary/${centerId}/enabled`, { enabled });
      setAiSecStats(prev => ({
        ...prev,
        [centerId]: { ...prev[centerId], enabled, voiceLimitReached: enabled ? false : prev[centerId]?.voiceLimitReached },
      }));
      toast.success(enabled ? 'Secretaria IA activada' : 'Secretaria IA desactivada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleOpenAddMinutes = (center) => {
    setAddMinutesCenter(center);
    setAddMinutesValue('');
    setShowAddMinutesModal(true);
  };

  const handleAddMinutes = async () => {
    const minutes = parseInt(addMinutesValue, 10);
    if (!minutes || minutes <= 0) {
      toast.error(t('success.centers.invalidMinutes'));
      return;
    }
    setAddMinutesLoading(true);
    try {
      const res = await api.put(`/admin/ai-secretary/${addMinutesCenter.id}/add-minutes`, { minutes });
      setAiSecStats(prev => ({ ...prev, [addMinutesCenter.id]: { ...prev[addMinutesCenter.id], ...res.data } }));
      fetchAiSecStats();
      setShowAddMinutesModal(false);
      toast.success(`${minutes} minutos añadidos correctamente`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al añadir minutos');
    } finally {
      setAddMinutesLoading(false);
    }
  };

  const handleOpenWaCredits = (center) => {
    setWaCreditsCenter(center);
    setWaCreditsValue('');
    setShowWaCreditsModal(true);
  };

  const handleAddWaCredits = async () => {
    const credits = parseInt(waCreditsValue, 10);
    if (!credits || credits <= 0) {
      toast.error(t('success.centers.invalidCredits'));
      return;
    }
    setWaCreditsLoading(true);
    try {
      const res = await api.put(`/admin/centers/${waCreditsCenter.id}/whatsapp-reminder-credits`, { credits });
      setCenters(prev => prev.map(c =>
        c.id === waCreditsCenter.id ? { ...c, whatsappReminderCredits: res.data.whatsappReminderCredits } : c
      ));
      setShowWaCreditsModal(false);
      toast.success(`${credits} créditos WhatsApp añadidos — total: ${res.data.whatsappReminderCredits}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al añadir créditos');
    } finally {
      setWaCreditsLoading(false);
    }
  };

  const handleSaveAssistantId = async (centerId) => {
    setAssistantIdSaving(prev => ({ ...prev, [centerId]: true }));
    try {
      await api.put(`/admin/ai-secretary/${centerId}/vapi-config`, {
        assistantId: assistantIdDraft[centerId] || '',
        phoneNumberId: phoneNumberIdDraft[centerId] || '',
      });
      setAiSecStats(prev => ({
        ...prev,
        [centerId]: {
          ...prev[centerId],
          assistantId: assistantIdDraft[centerId] || '',
          phoneNumberId: phoneNumberIdDraft[centerId] || '',
        },
      }));

      await api.post(`/admin/ai-secretary/${centerId}/sync-vapi`);

      toast.success(t('success.centers.vapiSynced'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally {
      setAssistantIdSaving(prev => ({ ...prev, [centerId]: false }));
    }
  };

  return (
    <div className="p-4 md:p-6 h-full overflow-y-auto" data-testid="centers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('centers.title')}
        </h1>
        
        <Button
          className="btn-primary"
          onClick={() => handleOpenDialog()}
          data-testid="add-center-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('centers.addCenter')}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : centers.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {t('centers.noCenters') || 'No centers found'}
        </div>
      ) : (
        <div className="grid gap-3">
          {centers.map((center) => {
            const admin = centerAdmins[center.id];
            const waStatus = center.whatsappStatus || 'not_connected';
            const waCfg = WA_STATUS_CONFIG[waStatus] || WA_STATUS_CONFIG.not_connected;
            const credits = center.whatsappReminderCredits;
            const creditsExhausted = credits !== null && credits !== undefined && credits <= 0;
            const aiSec = aiSecStats[center.id];
            const sub = subscriptions[center.id];
            const cf = centerForms[center.id] || {};
            const website = centerWebsiteDrafts[center.id] || buildCenterWebsiteDraft(centerWebsites[center.id]);

            return (
              <div
                key={center.id}
                className={`bg-card border border-zinc-800 rounded-sm overflow-hidden ${center.active === false ? 'opacity-60' : ''}`}
                data-testid={`center-row-${center.id}`}
              >
                {/* ── Card header ── */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                  onClick={() => toggleExpanded(center.id)}
                >
                  <Building2 className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{center.name}</span>
                    <span className="text-xs text-zinc-500 ml-2">{center.timezone}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ${center.active !== false ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                    {center.active !== false ? 'activo' : 'inactivo'}
                  </span>
                  {center.active !== false ? (
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); deactivateCenter(e, center.id); }} title={t('centers.deactivate')} data-testid={`deactivate-center-${center.id}`}>
                      <Power className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button variant="outline" size="icon" onClick={(e) => { e.stopPropagation(); reactivateCenter(e, center.id); }} title={t('centers.reactivate')} data-testid={`reactivate-center-${center.id}`}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="destructive" size="icon" onClick={(e) => openOffboardDialog(e, center)} title={t('centers.deleteHard')} data-testid={`delete-center-${center.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => handleImpersonate(e, center)}
                    disabled={!!impersonating[center.id]}
                    title="Entrar como admin"
                    data-testid={`impersonate-center-${center.id}`}
                  >
                    {impersonating[center.id]
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <LogIn className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleExportClients(e, center.id)}
                    disabled={exportingCenterId === center.id}
                    title={t('centers.exportClients')}
                    data-testid={`export-clients-csv-${center.id}`}
                  >
                    {exportingCenterId === center.id
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Download className="w-3 h-3" />}
                    {exportingCenterId === center.id
                      ? t('centers.exportClientsLoading')
                      : t('centers.exportClients')}
                  </Button>
                  {expandedCenters[center.id]
                    ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  }
                </div>

                {/* ── Tabs (only when expanded) ── */}
                {expandedCenters[center.id] && <Tabs defaultValue="general" className="px-4 pb-4 pt-3 border-t border-zinc-800">
                  <TabsList className="bg-zinc-900 border border-zinc-800 mb-4">
                    <TabsTrigger value="general" className="data-[state=active]:bg-zinc-800 text-xs">General</TabsTrigger>
                    <TabsTrigger value="subscription" className="data-[state=active]:bg-zinc-800 text-xs">Suscripción</TabsTrigger>
                    <TabsTrigger value="whatsapp" className="data-[state=active]:bg-zinc-800 text-xs">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: waCfg.dot }} />
                        WhatsApp
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="emails" className="data-[state=active]:bg-zinc-800 text-xs">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        Emails
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="ai-secretary" className="data-[state=active]:bg-zinc-800 text-xs">
                      <span className="flex items-center gap-1">
                        <Bot className="w-3 h-3" />
                        Secretaria IA
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="billing" className="data-[state=active]:bg-zinc-800 text-xs">Facturación</TabsTrigger>
                    {user?.role === 'SUPER_ADMIN' && (
                      <TabsTrigger value="public-website" className="data-[state=active]:bg-zinc-800 text-xs">
                        Web pública
                      </TabsTrigger>
                    )}
                  </TabsList>

                  {/* ── Tab: General ── */}
                  <TabsContent value="general">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium text-zinc-200">{center.name}</p>
                          <p className="text-xs text-zinc-500">{center.timezone}</p>
                        </div>
                        <Button size="sm" variant="outline" className="text-xs h-7 flex-shrink-0" onClick={() => handleOpenDialog(center)} data-testid={`edit-center-${center.id}`}>
                          <Edit className="w-3 h-3 mr-1" /> Editar nombre/zona
                        </Button>
                      </div>

                      {admin && (
                        <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
                          <div>
                            <p className="text-sm text-zinc-200">{admin.fullName}</p>
                            <p className="text-xs text-zinc-500">{admin.email}</p>
                          </div>
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => handleResetCenterAdminPassword(admin)}>
                            🔑 {t('centers.resetPassword')}
                          </Button>
                        </div>
                      )}

                      {/* Ficha sections */}
                      <div className="border-t border-zinc-800 pt-3 space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wide">{t('centers.fichaLabel')}</p>
                        {(() => {
                          const enabledSections = normalizeFichaSections(cf.clientFichaSections);
                          const orderedKeys = buildFichaSectionOrder(cf.clientFichaOrder, enabledSections);
                          const toggle = (key, checked) => {
                            setCenterForms(prev => {
                              const cur = prev[center.id] || {};
                              const enabled = normalizeFichaSections(cur.clientFichaSections);
                              const order = buildFichaSectionOrder(cur.clientFichaOrder, enabled);
                              const next = checked ? order.filter(k => enabled.includes(k) || k === key) : enabled.filter(k => k !== key);
                              return { ...prev, [center.id]: { ...cur, clientFichaSections: next, clientFichaOrder: order } };
                            });
                          };
                          const move = (key, direction) => {
                            setCenterForms(prev => {
                              const cur = prev[center.id] || {};
                              const enabled = normalizeFichaSections(cur.clientFichaSections);
                              const order = buildFichaSectionOrder(cur.clientFichaOrder, enabled);
                              const nextOrder = moveFichaSection(order, key, direction);
                              return { ...prev, [center.id]: { ...cur, clientFichaOrder: nextOrder, clientFichaSections: nextOrder.filter(k => enabled.includes(k)) } };
                            });
                          };
                          return (
                            <div className="space-y-1">
                              {orderedKeys.map((key, idx) => {
                                const isEnabled = enabledSections.includes(key);
                                return (
                                  <div key={key} className="flex items-center justify-between gap-2">
                                    <label className="flex items-center gap-2 text-xs text-zinc-300">
                                      <input type="checkbox" checked={isEnabled} onChange={e => toggle(key, e.target.checked)} />
                                      {t(`clientProfile.sections.${key}`)}
                                    </label>
                                    <div className="flex gap-1">
                                      <button type="button" className="p-0.5 rounded-sm border border-zinc-800 disabled:opacity-30" disabled={!isEnabled || idx === 0} onClick={() => move(key, 'up')}><ChevronUp className="w-3 h-3" /></button>
                                      <button type="button" className="p-0.5 rounded-sm border border-zinc-800 disabled:opacity-30" disabled={!isEnabled || idx === orderedKeys.length - 1} onClick={() => move(key, 'down')}><ChevronDown className="w-3 h-3" /></button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {/* RGPD */}
                      <div className="border-t border-zinc-800 pt-3 space-y-2">
                        <Label className="text-xs text-zinc-500 uppercase tracking-wide">{t('centers.rgpdLegalText')}</Label>
                        <Textarea
                          value={cf.rgpdLegalText || ''}
                          onChange={e => setCenterForms(prev => ({ ...prev, [center.id]: { ...prev[center.id], rgpdLegalText: e.target.value } }))}
                          className="input-tactical min-h-[80px] text-xs"
                        />
                      </div>

                      {/* Credenciales WhatsApp: se gestionan SOLO desde la pestaña "WhatsApp"
                          (Provisión de WhatsApp). No van en PUT /centers (guard T-46). */}
                      <div className="border-t border-zinc-800 pt-3">
                        <p className="text-xs text-zinc-500">{t('centers.whatsappManagedElsewhere')}</p>
                      </div>

                      <div className="flex justify-end pt-1">
                        <Button size="sm" className="btn-primary text-xs" onClick={() => handleSaveCenterGeneral(center.id)} disabled={!!centerFormSaving[center.id]}>
                          {centerFormSaving[center.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                          Guardar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ── Tab: Suscripción ── */}
                  <TabsContent value="subscription">
                    {sub ? (
                      <div className="space-y-2 text-sm">
                        {[
                          ['Plan', `${sub.plan || '—'} · ${sub.planPriceEur ?? 0}€/mes`],
                          ['Estado', sub.status || '—'],
                          ['MRR', `${sub.mrrEur ?? 0}€/mes`],
                        ].map(([label, value]) => (
                          <div key={label} className="flex justify-between">
                            <span className="text-zinc-500">{label}</span>
                            <span className="text-zinc-200">{value}</span>
                          </div>
                        ))}
                        {(sub.addons || []).length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-zinc-500">Addons</span>
                            <span className="text-zinc-200">{sub.addons.map(a => `${a.addonId} +${a.priceEur ?? 0}€`).join(', ')}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-zinc-500">Sin suscripción activa</p>
                    )}
                  </TabsContent>

                  {/* ── Tab: WhatsApp ── */}
                  <TabsContent value="whatsapp">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${waCfg.color}`}>
                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: waCfg.dot }} />
                            {t(`centers.whatsapp.status.${waStatusKey(waStatus)}`)}
                          </span>
                          {credits !== null && credits !== undefined && (
                            <span className={`text-xs px-2 py-0.5 rounded ${creditsExhausted ? 'bg-red-500/10 text-red-400' : credits <= 20 ? 'bg-yellow-500/10 text-yellow-400' : 'bg-zinc-700 text-zinc-300'}`}>
                              {creditsExhausted ? '0 créditos' : `${credits} créditos`}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={() => handleWaRefresh(center.id)} disabled={!!waRefreshing[center.id]} title={t('centers.whatsapp.refreshStatus')} data-testid={`refresh-wa-${center.id}`}>
                            <RefreshCw className={`w-4 h-4 ${waRefreshing[center.id] ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => handleOpenWaCredits(center)} data-testid={`wa-credits-${center.id}`}>
                            <Plus className="w-3 h-3 mr-1" /> Créditos
                          </Button>
                        </div>
                      </div>
                      <div className="border-t border-zinc-800 pt-3">
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="text-xs" onClick={() => openWaDialog(center)} data-testid={`provision-wa-${center.id}`}>
                            <Wifi className="w-3 h-3 mr-1" /> {t('centers.whatsapp.provisionBtn')}
                          </Button>
                          {waStatus !== 'not_connected' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="text-xs"
                              data-testid={`deprovision-wa-${center.id}`}
                              onClick={async () => {
                                if (!window.confirm('¿Seguro que quieres desvincular WhatsApp de este centro? Los clientes dejarán de recibir recordatorios por WhatsApp.')) return;
                                try {
                                  await api.delete(`/admin/centers/${center.id}/whatsapp`);
                                  setCenters(cs => cs.map(c =>
                                    c.id === center.id
                                      ? { ...c, whatsappStatus: 'not_connected', whatsappPhoneNumberId: null, whatsappAccessToken: null }
                                      : c
                                  ));
                                  toast.success(t('success.centers.whatsappUnlinked'));
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Error al desvincular WhatsApp.');
                                }
                              }}
                            >
                              <WifiOff className="w-3 h-3 mr-1" /> Desvincular
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ── Tab: Emails ── */}
                  <TabsContent value="emails">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${center.emailRemindersEnabled === false ? 'bg-zinc-700 text-zinc-400' : 'bg-green-500/10 text-green-400'}`}>
                            <span className={`w-2 h-2 rounded-full inline-block ${center.emailRemindersEnabled === false ? 'bg-zinc-500' : 'bg-green-400'}`} />
                            {center.emailRemindersEnabled === false ? 'Pausado' : 'Activo'}
                          </span>
                        </div>
                        <Switch
                          checked={center.emailRemindersEnabled !== false}
                          onCheckedChange={v => handleEmailRemindersToggle(center.id, v)}
                          data-testid={`email-reminders-toggle-${center.id}`}
                        />
                      </div>
                      <p className="text-xs text-zinc-500">
                        Cuando está pausado, ningún cliente del centro recibirá recordatorios por email.
                      </p>
                      <div className="border-t border-zinc-800 pt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-300 font-medium">Resumen diario del planner</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${center.dailyPlannerEmailEnabled ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                              <span className={`w-1.5 h-1.5 rounded-full inline-block ${center.dailyPlannerEmailEnabled ? 'bg-green-400' : 'bg-zinc-500'}`} />
                              {center.dailyPlannerEmailEnabled ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                          <Switch
                            checked={!!center.dailyPlannerEmailEnabled}
                            onCheckedChange={v => handleDailyPlannerEmailToggle(center.id, v)}
                            data-testid={`daily-planner-email-toggle-${center.id}`}
                          />
                        </div>
                        <p className="text-xs text-zinc-500">
                          Envía un email cada mañana a los administradores del centro con todas las citas del día.
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ── Tab: Secretaria IA ── */}
                  <TabsContent value="ai-secretary">
                    <div className="space-y-4">
                      <div className="flex items-center gap-6 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400">Paquete IA</span>
                          <Switch checked={center.aiSecretaryEnabled ?? false} onCheckedChange={v => handleAiSecPackageToggle(center.id, v)} data-testid={`ai-sec-package-toggle-${center.id}`} />
                        </div>
                        {center.aiSecretaryEnabled && aiSec && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-400">Estado</span>
                            <Switch checked={aiSec.enabled ?? false} onCheckedChange={v => handleAiSecToggle(center.id, v)} data-testid={`ai-sec-toggle-${center.id}`} />
                            <span className={`text-xs px-1.5 py-0.5 rounded ${aiSec.voiceLimitReached ? 'bg-red-500/10 text-red-400' : aiSec.enabled ? 'bg-green-500/10 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
                              {aiSec.voiceLimitReached ? 'Límite' : aiSec.enabled ? 'Activa' : 'Inactiva'}
                            </span>
                          </div>
                        )}
                      </div>

                      {center.aiSecretaryEnabled && aiSec && (
                        <>
                          <div className="space-y-1 border-t border-zinc-800 pt-3">
                            <div className="flex justify-between text-xs text-zinc-400">
                              <span>Minutos usados</span>
                              <span>{(aiSec.voiceMinutesUsed || 0).toFixed(1)} / {aiSec.voiceMinutesIncluded || 60} min</span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${aiSec.voiceLimitReached ? 'bg-red-500' : 'bg-green-500'}`} style={{ width: `${Math.min(100, ((aiSec.voiceMinutesUsed || 0) / (aiSec.voiceMinutesIncluded || 60)) * 100)}%` }} />
                            </div>
                            <div className="flex justify-end pt-1">
                              <Button variant="outline" size="sm" className="text-xs" onClick={() => handleOpenAddMinutes(center)} data-testid={`ai-sec-add-minutes-${center.id}`}>
                                <Plus className="w-3 h-3 mr-1" /> Añadir minutos
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2 border-t border-zinc-800 pt-3">
                            <Input value={assistantIdDraft[center.id] ?? ''} onChange={e => setAssistantIdDraft(prev => ({ ...prev, [center.id]: e.target.value }))} placeholder="Vapi Assistant ID" className="input-tactical h-7 text-xs" data-testid={`assistant-id-input-${center.id}`} />
                            <div className="flex items-center gap-2">
                              <Input value={phoneNumberIdDraft[center.id] ?? ''} onChange={e => setPhoneNumberIdDraft(prev => ({ ...prev, [center.id]: e.target.value }))} placeholder="Vapi Phone Number ID" className="input-tactical h-7 text-xs flex-1" />
                              <Button size="sm" variant="outline" className="text-xs h-7 px-2 flex-shrink-0" onClick={() => handleSaveAssistantId(center.id)} disabled={!!assistantIdSaving[center.id]} data-testid={`assistant-id-save-${center.id}`}>
                                {assistantIdSaving[center.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar + Sync'}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </TabsContent>

                  {/* ── Tab: Facturación ── */}
                  <TabsContent value="billing">
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Razón social</label>
                        <Input value={cf.fiscalName || ''} onChange={e => setCenterForms(prev => ({ ...prev, [center.id]: { ...prev[center.id], fiscalName: e.target.value } }))} className="input-tactical text-xs h-8" placeholder="Empresa S.L." />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">CIF / NIF</label>
                        <Input value={cf.fiscalId || ''} onChange={e => setCenterForms(prev => ({ ...prev, [center.id]: { ...prev[center.id], fiscalId: e.target.value } }))} className="input-tactical text-xs h-8" placeholder="B12345678" />
                      </div>
                      <div>
                        <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Email de facturación</label>
                        <Input value={cf.billingEmail || ''} onChange={e => setCenterForms(prev => ({ ...prev, [center.id]: { ...prev[center.id], billingEmail: e.target.value } }))} className="input-tactical text-xs h-8" placeholder="facturacion@empresa.com" type="email" />
                      </div>
                      <div className="flex justify-end pt-1">
                        <Button size="sm" className="btn-primary text-xs" onClick={() => handleSaveCenterGeneral(center.id)} disabled={!!centerFormSaving[center.id]}>
                          {centerFormSaving[center.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                          Guardar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                  {user?.role === 'SUPER_ADMIN' && (
                    <TabsContent value="public-website">
                      <div className="space-y-3" data-testid={`public-website-${center.id}`}>
                        <div>
                          <p className="text-sm font-medium text-zinc-200">Web pública</p>
                          <p className="text-xs text-zinc-500">Configuración opcional en www.adamsched.com/&lt;slug&gt;</p>
                        </div>
                        {centerWebsites[center.id] ? (
                          <div className="space-y-3">
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Slug</label>
                                <Input value={website.slug} onChange={e => updateCenterWebsiteDraft(center.id, 'slug', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-slug-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Estado</label>
                                <p className="text-xs text-zinc-400 pt-2">{website.status}</p>
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Título</label>
                                <Input value={website.meta.title} onChange={e => updateCenterWebsiteDraft(center.id, 'meta.title', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-meta-title-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Descripción</label>
                                <Input value={website.meta.description} onChange={e => updateCenterWebsiteDraft(center.id, 'meta.description', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-meta-description-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Imagen SEO</label>
                                <Input value={website.meta.ogImageUrl} onChange={e => updateCenterWebsiteDraft(center.id, 'meta.ogImageUrl', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-og-image-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Headline</label>
                                <Input value={website.content.hero.headline} onChange={e => updateCenterWebsiteDraft(center.id, 'content.hero.headline', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-hero-headline-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Logo URL</label>
                                <Input value={website.brand.logoUrl} onChange={e => updateCenterWebsiteDraft(center.id, 'brand.logoUrl', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-logo-url-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Color primario</label>
                                <Input value={website.brand.primaryColor} onChange={e => updateCenterWebsiteDraft(center.id, 'brand.primaryColor', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-primary-color-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Color acento</label>
                                <Input value={website.brand.accentColor} onChange={e => updateCenterWebsiteDraft(center.id, 'brand.accentColor', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-accent-color-${center.id}`} />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Subheadline</label>
                              <Textarea value={website.content.hero.subheadline} onChange={e => updateCenterWebsiteDraft(center.id, 'content.hero.subheadline', e.target.value)} className="input-tactical text-xs" rows={2} data-testid={`website-hero-subheadline-${center.id}`} />
                            </div>
                            {/* F4 fix (review round 1): ctaLabel/ctaUrl are part of
                                the D9 hero schema, already round-tripped by
                                buildCenterWebsiteDraft and accepted by the backend
                                schema, but had no input — unreachable from the
                                admin UI. Wired through the same updateCenterWebsiteDraft
                                helper as the other hero fields. */}
                            <div className="grid md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">CTA — texto del botón</label>
                                <Input value={website.content.hero.ctaLabel} onChange={e => updateCenterWebsiteDraft(center.id, 'content.hero.ctaLabel', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-hero-cta-label-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">CTA — URL</label>
                                <Input value={website.content.hero.ctaUrl} onChange={e => updateCenterWebsiteDraft(center.id, 'content.hero.ctaUrl', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-hero-cta-url-${center.id}`} />
                              </div>
                            </div>
                            <div data-testid={`website-sections-${center.id}`}>
                              <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Secciones</label>
                              <div className="space-y-3">
                                {(website.content.sections || []).map((section, idx) => (
                                  <div key={`section-${idx}`} className="border border-zinc-800 rounded p-3 space-y-2 bg-zinc-900/30" data-testid={`website-section-block-${section.type}-${idx}-${center.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <select
                                        value={section.type}
                                        onChange={e => {
                                          // Re-review finding (ui-ux/HIGH, §9.5): the
                                          // discriminated union has zero structural field
                                          // overlap across the 7 types — switching the
                                          // type without a guard would wipe everything
                                          // the operator just typed (heading/body,
                                          // items[], photoIds[], address/mapEmbed).
                                          // Gate the destructive action behind a
                                          // window.confirm so accidental clicks cannot
                                          // destroy content.
                                          const newType = e.target.value;
                                          if (newType === section.type) return;
                                          const proceed = window.confirm(
                                            'Cambiar el tipo borrará el contenido específico de esta sección y la dejará con los campos vacíos del nuevo tipo. ¿Continuar?'
                                          );
                                          if (proceed) {
                                            updateContentSection(center.id, idx, createEmptySection(newType));
                                          }
                                          // On cancel: no state update. The controlled
                                          // <select> will snap back to `section.type`
                                          // on the next render (which any state change
                                          // in the editor triggers). The brief flash is
                                          // acceptable vs. the alternative of turning the
                                          // type-selector into an uncontrolled widget.
                                        }}
                                        className="input-tactical text-xs h-8"
                                        data-testid={`website-section-type-${idx}-${center.id}`}
                                      >
                                        {SECTION_TYPE_OPTIONS.map(opt => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                      <Button type="button" size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => removeContentSection(center.id, idx)} data-testid={`website-section-remove-${idx}-${center.id}`}>
                                        Eliminar sección
                                      </Button>
                                    </div>

                                    {section.type === 'text' && (
                                      <div className="space-y-2">
                                        <Input
                                          value={section.heading || ''}
                                          onChange={e => updateContentSection(center.id, idx, { ...section, heading: e.target.value })}
                                          placeholder="Encabezado"
                                          className="input-tactical text-xs h-8"
                                          data-testid={`website-section-text-heading-${idx}-${center.id}`}
                                        />
                                        <Textarea
                                          value={section.body || ''}
                                          onChange={e => updateContentSection(center.id, idx, { ...section, body: e.target.value })}
                                          placeholder="Cuerpo del texto"
                                          className="input-tactical text-xs"
                                          rows={3}
                                          data-testid={`website-section-text-body-${idx}-${center.id}`}
                                        />
                                      </div>
                                    )}

                                    {section.type === 'services' && renderSectionItems(section, idx, [['name', 'Nombre'], ['description', 'Descripción']], center.id)}

                                    {section.type === 'schedule' && renderSectionItems(section, idx, [['day', 'Día'], ['hours', 'Horario']], center.id)}

                                    {section.type === 'gallery' && (
                                      <div className="space-y-2">
                                        <p className="text-xs text-zinc-400">Selecciona las fotos del centro que aparecerán en la galería.</p>
                                        {(website.assets?.photos || []).length === 0 ? (
                                          <p className="text-xs text-amber-400" data-testid={`website-section-gallery-empty-${idx}-${center.id}`}>
                                            Sube fotos en la sección de Assets para poder seleccionarlas aquí.
                                          </p>
                                        ) : (
                                          // center-website-gallery-black-screen (R1/R3, ARIA fix per
                                          // sdd-review-ui-ux HIGH): replaced the
                                          // <label><input type="checkbox" className="sr-only"></label>
                                          // pattern with an explicit <div role="checkbox"
                                          // aria-checked> because clicking the label focused the
                                          // hidden input and the browser then called
                                          // scrollIntoViewIfNeeded on it, which propagated to
                                          // document-level scroll inside the `overflow-hidden`
                                          // <main> + `h-screen` <div> + Centers `overflow-y-auto`
                                          // layout. Symptom was the document keeping scrollY=1816
                                          // with the React root now above the viewport
                                          // (user-visible as a "black" body bg);
                                          // window.scrollTo(0,0) restored the UI. We keep the
                                          // (decorative) native <input> so the existing
                                          // `data-testid="website-section-gallery-checkbox-…"`
                                          // contracts keep working and AT/forms-mode users still
                                          // see a checkbox in the DOM; the input is readOnly,
                                          // tabIndex={-1}, aria-hidden, so it does NOT receive
                                          // programmatic focus and does NOT trigger
                                          // scrollIntoView. We expose the toggle as
                                          // role="checkbox" + aria-checked (NOT role="button" +
                                          // aria-pressed — that was a semantic mismatch with
                                          // every other checkbox toggle in this editor).
                                          // Pressing Space or Enter on the focused card
                                          // triggers the same toggle (kebab/AT parity).
                                          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                            {(website.assets.photos || []).map(photo => {
                                              const selected = (section.photoIds || []).includes(photo.id);
                                              // center-website-gallery-black-screen §3.3: only ids
                                              // that exist in this center's assets.photos may
                                              // end up in photoIds. The setter below enforces it
                                              // on every toggle.
                                              const validIds = new Set((website.assets.photos || []).map(p => p.id));
                                              const toggle = () => {
                                                const current = (section.photoIds || []).filter(id => validIds.has(id));
                                                const next = current.includes(photo.id)
                                                  ? current.filter(id => id !== photo.id)
                                                  : [...current, photo.id];
                                                updateContentSection(center.id, idx, { ...section, photoIds: next });
                                              };
                                              return (
                                                <div
                                                  key={photo.id}
                                                  role="checkbox"
                                                  tabIndex={0}
                                                  aria-checked={selected}
                                                  aria-label={photo.alt || photo.id}
                                                  className={`cursor-pointer border rounded p-1 select-none ${selected ? 'border-amber-400' : 'border-zinc-700'}`}
                                                  data-testid={`website-section-gallery-photo-${photo.id}-${idx}-${center.id}`}
                                                  onClick={toggle}
                                                  onKeyDown={e => {
                                                    if (e.key === ' ' || e.key === 'Enter') {
                                                      e.preventDefault();
                                                      toggle();
                                                    }
                                                  }}
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={selected}
                                                    readOnly
                                                    tabIndex={-1}
                                                    aria-hidden="true"
                                                    className="sr-only"
                                                    data-testid={`website-section-gallery-checkbox-${photo.id}-${idx}-${center.id}`}
                                                  />
                                                  <img src={photo.url} alt={photo.alt || 'Foto'} className="h-16 w-full object-cover rounded" />
                                                  <p className="text-[10px] text-zinc-400 text-center mt-1 truncate">{photo.alt || photo.id}</p>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {section.type === 'pricing' && renderSectionItems(section, idx, [['name', 'Nombre'], ['price', 'Precio'], ['period', 'Periodo']], center.id)}

                                    {section.type === 'testimonials' && renderSectionItems(section, idx, [['quote', 'Cita'], ['author', 'Autor']], center.id)}

                                    {section.type === 'location' && (
                                      <div className="space-y-2">
                                        <Input
                                          value={section.address || ''}
                                          onChange={e => updateContentSection(center.id, idx, { ...section, address: e.target.value })}
                                          placeholder="Dirección pública del centro"
                                          className="input-tactical text-xs h-8"
                                          data-testid={`website-section-location-address-${idx}-${center.id}`}
                                        />
                                        <Input
                                          value={section.mapEmbed || ''}
                                          onChange={e => updateContentSection(center.id, idx, { ...section, mapEmbed: e.target.value })}
                                          placeholder="URL https://www.google.com/maps/embed?pb=... (opcional)"
                                          className="input-tactical text-xs h-8"
                                          data-testid={`website-section-location-map-${idx}-${center.id}`}
                                        />
                                        <p className="text-[10px] text-zinc-500">La URL debe ser del tipo https://www.google.com/maps/embed?pb=...</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <div className="flex flex-wrap gap-1 pt-1 border-t border-zinc-800">
                                  {SECTION_TYPE_OPTIONS.map(opt => (
                                    <Button
                                      key={opt.value}
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="text-xs"
                                      onClick={() => addContentSection(center.id, opt.value)}
                                      data-testid={`website-section-add-${opt.value}-${center.id}`}
                                    >
                                      + {opt.label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                              <Input value={website.links.bookingUrl} onChange={e => updateCenterWebsiteDraft(center.id, 'links.bookingUrl', e.target.value)} placeholder="URL de reserva (opcional)" className="input-tactical text-xs h-8" data-testid={`website-booking-url-${center.id}`} />
                              <Input value={website.links.clientLoginUrl} onChange={e => updateCenterWebsiteDraft(center.id, 'links.clientLoginUrl', e.target.value)} placeholder="URL de acceso cliente (opcional)" className="input-tactical text-xs h-8" data-testid={`website-client-login-url-${center.id}`} />
                            </div>
                            <div className="space-y-2 border-t border-zinc-800 pt-3">
                              <p className="text-xs text-zinc-500 uppercase tracking-wide">Legal y permisos</p>
                              <div className="grid md:grid-cols-2 gap-3">
                                <label className="text-xs text-zinc-400 flex items-center gap-2">
                                  <input type="checkbox" checked={website.legal.reuseExistingLogo} onChange={e => updateCenterWebsiteDraft(center.id, 'legal.reuseExistingLogo', e.target.checked)} data-testid={`website-reuse-logo-${center.id}`} />
                                  Logo reutilizable autorizado
                                </label>
                                <label className="text-xs text-zinc-400 flex items-center gap-2">
                                  <input type="checkbox" checked={website.legal.reuseExistingPhotos} onChange={e => updateCenterWebsiteDraft(center.id, 'legal.reuseExistingPhotos', e.target.checked)} data-testid={`website-reuse-photos-${center.id}`} />
                                  Fotos reutilizables autorizadas
                                </label>
                              </div>
                              <Input value={website.legal.oldWebsite} onChange={e => updateCenterWebsiteDraft(center.id, 'legal.oldWebsite', e.target.value)} placeholder="Web anterior (opcional)" className="input-tactical text-xs h-8" data-testid={`website-old-url-${center.id}`} />
                            </div>
                            <div className="space-y-3 border-t border-zinc-800 pt-3">
                              <p className="text-xs text-zinc-500 uppercase tracking-wide">Assets</p>
                              <div className="flex flex-wrap items-center gap-2">
                                <input ref={node => { websiteFileInputs.current[`${center.id}-logo`] = node; }} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => uploadCenterWebsiteAsset(center.id, 'logo', e.target.files?.[0])} />
                                {/* Re-review finding (ui-ux/HIGH, §9.10): uploading a
                                    logo when one already exists overwrites (and
                                    best-effort deletes) the previous Blob — a
                                    destructive replace. Confirm before opening the
                                    picker when a logo is present; a first upload
                                    (no existing logo) is non-destructive and skips it. */}
                                <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => { if (website.brand.logoUrl && !window.confirm('Se reemplazará el logo actual y se eliminará el anterior del almacenamiento de forma permanente. ¿Continuar?')) return; websiteFileInputs.current[`${center.id}-logo`]?.click(); }} disabled={!!centerWebsiteUploadLoading[`${center.id}-logo`]} data-testid={`upload-logo-${center.id}`}>
                                  {centerWebsiteUploadLoading[`${center.id}-logo`] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Subir logo
                                </Button>
                                <input ref={node => { websiteFileInputs.current[`${center.id}-photo`] = node; }} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => uploadCenterWebsiteAsset(center.id, 'photo', e.target.files?.[0])} />
                                <Button type="button" size="sm" variant="outline" className="text-xs" onClick={() => websiteFileInputs.current[`${center.id}-photo`]?.click()} disabled={!!centerWebsiteUploadLoading[`${center.id}-photo`]} data-testid={`upload-photo-${center.id}`}>
                                  {centerWebsiteUploadLoading[`${center.id}-photo`] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Subir foto
                                </Button>
                              </div>
                              {website.brand.logoUrl && <div className="flex items-center gap-2 text-xs text-zinc-400"><img src={website.brand.logoUrl} alt="Logo" className="h-10 w-10 object-contain rounded border border-zinc-700" /><Button type="button" size="sm" variant="ghost" className="text-xs text-red-400" onClick={() => deleteCenterWebsiteAsset(center.id, 'logo')}>Eliminar logo</Button></div>}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {website.assets.photos.map(photo => <div key={photo.id} className="space-y-1">
                                  <img src={photo.url} alt={photo.alt || 'Foto'} className="h-20 w-full object-cover rounded border border-zinc-700" />
                                  {/* Re-review finding (ui-ux/MEDIUM, §9.12): editable
                                      alt text — a required D9 field with no prior editor. */}
                                  <Input value={photo.alt || ''} onChange={e => updateCenterWebsitePhotoAlt(center.id, photo.id, e.target.value)} placeholder="Texto alternativo (alt)" className="input-tactical text-xs h-7" data-testid={`website-photo-alt-${photo.id}-${center.id}`} />
                                  <input ref={node => { websiteFileInputs.current[`${center.id}-photo-${photo.id}`] = node; }} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={e => uploadCenterWebsiteAsset(center.id, 'photo', e.target.files?.[0], photo.id)} />
                                  {/* Re-review finding (ui-ux/HIGH, §9.10): replacing a
                                      photo overwrites and best-effort deletes the previous
                                      Blob — confirm before opening the picker. */}
                                  <Button type="button" size="sm" variant="outline" className="text-xs w-full" onClick={() => { if (!window.confirm('Se reemplazará esta foto y se eliminará la anterior del almacenamiento de forma permanente. ¿Continuar?')) return; websiteFileInputs.current[`${center.id}-photo-${photo.id}`]?.click(); }}>Reemplazar</Button>
                                  <Button type="button" size="sm" variant="ghost" className="text-xs text-red-400 w-full" onClick={() => deleteCenterWebsiteAsset(center.id, 'photo', photo.id)}>Eliminar</Button>
                                </div>)}
                              </div>
                            </div>
                            {centerWebsiteErrors[center.id] && <p className="text-xs text-red-400" data-testid={`website-error-${center.id}`}>{centerWebsiteErrors[center.id]}</p>}
                            <div className="flex justify-end gap-2">
                              <Button size="sm" className="btn-primary text-xs" onClick={() => saveCenterWebsite(center.id)} disabled={!!centerWebsiteSaving[center.id]} data-testid={`save-public-website-${center.id}`}>
                                {centerWebsiteSaving[center.id] && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Guardar
                              </Button>
                              <Button size="sm" className="btn-primary text-xs" onClick={() => activateCenterWebsite(center.id)} disabled={!!centerWebsiteSaving[center.id]} data-testid={`activate-public-website-${center.id}`}>
                                {centerWebsiteSaving[center.id] && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Guardar y activar
                              </Button>
                              {/* §9.13: deactivation is only meaningful for a site
                                  that is currently ACTIVE (DELETE is a reversible
                                  soft-disable, D8); hide it otherwise. */}
                              {website.status === 'ACTIVE' && (
                                <Button size="sm" variant="outline" className="text-xs text-red-400" onClick={() => deactivateCenterWebsite(center.id)} disabled={!!centerWebsiteSaving[center.id]} data-testid={`deactivate-public-website-${center.id}`}>
                                  {centerWebsiteSaving[center.id] && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Desactivar
                                </Button>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <span className="text-xs text-zinc-400">Crear configuración inicial</span>
                            {/* Re-review finding (ui-ux/MEDIUM, §9.11): the website
                                slug is the contractual public URL and is independent
                                of the booking slug (D16/R2). Show it as an editable
                                field (pre-filled with the centre-derived default via
                                placeholder) plus a live URL preview, so the operator
                                reviews/overrides it BEFORE the POST instead of
                                discovering the auto-derived value only afterwards. */}
                            <div>
                              <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Slug (URL pública)</label>
                              <Input
                                value={website.slug}
                                onChange={e => updateCenterWebsiteDraft(center.id, 'slug', e.target.value)}
                                placeholder={deriveWebsiteSlug(center)}
                                className="input-tactical text-xs h-8"
                                data-testid={`website-create-slug-${center.id}`}
                              />
                              <p className="text-[10px] text-zinc-500 mt-1" data-testid={`website-create-slug-preview-${center.id}`}>
                                www.adamsched.com/{(website.slug || '').trim() || deriveWebsiteSlug(center)}
                              </p>
                            </div>
                            {/* F1 fix (review round 1): the backend requires
                                meta.title / meta.description / content.hero.headline
                                on create (no defaults) — collect the D15 minimum
                                fields here instead of POSTing a bare {slug, status}
                                that 422s every time. */}
                            <div className="grid md:grid-cols-3 gap-3">
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Título</label>
                                <Input value={website.meta.title} onChange={e => updateCenterWebsiteDraft(center.id, 'meta.title', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-create-meta-title-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Descripción</label>
                                <Input value={website.meta.description} onChange={e => updateCenterWebsiteDraft(center.id, 'meta.description', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-create-meta-description-${center.id}`} />
                              </div>
                              <div>
                                <label className="text-xs text-zinc-500 uppercase tracking-wide block mb-1">Headline</label>
                                <Input value={website.content.hero.headline} onChange={e => updateCenterWebsiteDraft(center.id, 'content.hero.headline', e.target.value)} className="input-tactical text-xs h-8" data-testid={`website-create-hero-headline-${center.id}`} />
                              </div>
                            </div>
                            {centerWebsiteErrors[center.id] && <p className="text-xs text-red-400" data-testid={`website-error-${center.id}`}>{centerWebsiteErrors[center.id]}</p>}
                            <div className="flex justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => createCenterWebsite(center)}
                                disabled={!!centerWebsiteSaving[center.id]}
                                data-testid={`create-public-website-${center.id}`}
                              >
                                {centerWebsiteSaving[center.id] && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                                Crear
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  )}
                </Tabs>}
              </div>
            );
          })}
        </div>
      )}

      {/* WhatsApp Provision Dialog */}
      <Dialog open={showWaDialog} onOpenChange={setShowWaDialog}>
        <DialogContent className="sm:max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2">
              <Wifi className="w-4 h-4" />
              {t('centers.whatsapp.provision.title')}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {t('centers.whatsapp.provision.title')}
            </DialogDescription>
          </DialogHeader>

          {waCenter && (
            <div className="text-sm text-zinc-400 -mt-2 mb-4 font-medium">{waCenter.name}</div>
          )}

          {showGuide ? (
            /* ── Onboarding guide (first-time setup) ── */
            <div className="space-y-0">
              {[
                {
                  num: 1,
                  title: t('centers.whatsapp.guide.step1.title'),
                  text: t('centers.whatsapp.guide.step1.text'),
                  extra: (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 text-xs"
                      onClick={() => window.open('https://business.facebook.com', '_blank')}
                    >
                      {t('centers.whatsapp.guide.step1.btn')}
                    </Button>
                  ),
                },
                {
                  num: 2,
                  title: t('centers.whatsapp.guide.step2.title'),
                  text: t('centers.whatsapp.guide.step2.text'),
                },
                {
                  num: 3,
                  title: t('centers.whatsapp.guide.step3.title'),
                  text: t('centers.whatsapp.guide.step3.text'),
                },
                {
                  num: 4,
                  title: t('centers.whatsapp.guide.step4.title'),
                  text: t('centers.whatsapp.guide.step4.text'),
                  extra: (
                    <Input
                      value={waFormData.phoneNumberId}
                      onChange={(e) => setWaFormData(f => ({ ...f, phoneNumberId: e.target.value }))}
                      className="input-tactical mt-2"
                      placeholder="1234567890"
                      data-testid="wa-phone-number-id-input"
                    />
                  ),
                },
                {
                  num: 5,
                  title: t('centers.whatsapp.guide.step5.title'),
                  text: t('centers.whatsapp.guide.step5.text'),
                  extra: (
                    <Input
                      value={waFormData.wabaId}
                      onChange={(e) => setWaFormData(f => ({ ...f, wabaId: e.target.value }))}
                      className="input-tactical mt-2"
                      placeholder="1234567890"
                      data-testid="wa-waba-id-input"
                    />
                  ),
                },
                {
                  num: 6,
                  title: t('centers.whatsapp.guide.step6.title'),
                  extra: (
                    <>
                      <Input
                        value={waFormData.pin}
                        onChange={(e) => setWaFormData(f => ({ ...f, pin: e.target.value }))}
                        className="input-tactical mt-2"
                        placeholder="123456"
                        maxLength={6}
                        inputMode="numeric"
                        data-testid="wa-pin-input"
                      />
                      <p className="text-xs text-zinc-500 mt-1">{t('centers.whatsapp.provision.pinHelp')}</p>
                    </>
                  ),
                },
              ].map((step, idx, arr) => (
                <div key={step.num} className="flex gap-3">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-7 h-7 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-xs font-bold text-zinc-300">
                      {step.num}
                    </div>
                    {idx < arr.length - 1 && (
                      <div className="w-px flex-1 bg-zinc-800 my-1 min-h-[12px]" />
                    )}
                  </div>
                  <div className={`flex-1 min-w-0 ${idx < arr.length - 1 ? 'pb-5' : 'pb-2'}`}>
                    <div className="text-sm font-semibold text-zinc-200 leading-snug">{step.title}</div>
                    {step.text && (
                      <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{step.text}</p>
                    )}
                    {step.extra && step.extra}
                  </div>
                </div>
              ))}

              {/* Access token (opcional — para verificación automática) */}
              <div className="mt-4 space-y-1">
                <Label>Access Token de Meta</Label>
                <Input
                  type="password"
                  value={waFormData.accessToken}
                  onChange={(e) => setWaFormData(f => ({ ...f, accessToken: e.target.value }))}
                  className="input-tactical"
                  placeholder="EAAxxxxxxxx... (opcional)"
                  data-testid="wa-access-token-input"
                />
                <p className="text-xs text-zinc-500">
                  Necesario para la verificación automática. Sin él, el número quedará en revisión pendiente.
                </p>
              </div>

              {/* Primary action */}
              <div className="pt-4 space-y-3">
                <Button
                  className="btn-primary w-full"
                  onClick={handleWaProvision}
                  disabled={waFormLoading || waEmbeddedLoading}
                  data-testid="wa-provision-submit-btn"
                >
                  {waFormLoading
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Wifi className="w-4 h-4 mr-2" />}
                  {t('centers.whatsapp.guide.connectBtn')}
                </Button>

                {/* Embedded Signup secondary option */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 border-t border-zinc-800" />
                  <span className="text-xs text-zinc-600 uppercase tracking-wider">
                    {t('centers.whatsapp.provision.orManual')}
                  </span>
                  <div className="flex-1 border-t border-zinc-800" />
                </div>
                <Button
                  className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold"
                  onClick={() => handleEmbeddedSignup(waCenter)}
                  disabled={waEmbeddedLoading || waFormLoading}
                  data-testid="wa-embedded-signup-btn"
                >
                  {waEmbeddedLoading
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Wifi className="w-4 h-4 mr-2" />}
                  {t('centers.whatsapp.provision.connectWithFacebook')}
                </Button>

                <div className="flex justify-start">
                  <Button variant="ghost" size="sm" onClick={() => setShowWaDialog(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* ── Quick edit form (already connected, updating credentials) ── */
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>{t('centers.whatsapp.provision.pin')} *</Label>
                <Input
                  value={waFormData.pin}
                  onChange={(e) => setWaFormData(f => ({ ...f, pin: e.target.value }))}
                  className="input-tactical"
                  placeholder="123456"
                  maxLength={6}
                  inputMode="numeric"
                  data-testid="wa-pin-input"
                />
                <p className="text-xs text-zinc-500">{t('centers.whatsapp.provision.pinHelp')}</p>
              </div>

              <div className="space-y-2">
                <Label>{t('centers.whatsapp.provision.wabaId')}</Label>
                <Input
                  value={waFormData.wabaId}
                  onChange={(e) => setWaFormData(f => ({ ...f, wabaId: e.target.value }))}
                  className="input-tactical"
                  placeholder="1234567890"
                  data-testid="wa-waba-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('centers.whatsapp.provision.phoneNumberId')}</Label>
                <Input
                  value={waFormData.phoneNumberId}
                  onChange={(e) => setWaFormData(f => ({ ...f, phoneNumberId: e.target.value }))}
                  className="input-tactical"
                  placeholder="1234567890"
                  data-testid="wa-phone-number-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('centers.whatsapp.provision.accessToken')}</Label>
                <Input
                  type="password"
                  value={waFormData.accessToken}
                  onChange={(e) => setWaFormData(f => ({ ...f, accessToken: e.target.value }))}
                  className="input-tactical"
                  placeholder="EAAxxxxxxx… (opcional)"
                  data-testid="wa-access-token-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('centers.whatsapp.provision.displayName')}</Label>
                <Input
                  value={waFormData.displayName}
                  onChange={(e) => setWaFormData(f => ({ ...f, displayName: e.target.value }))}
                  className="input-tactical"
                  data-testid="wa-display-name-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowWaDialog(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  className="btn-primary"
                  onClick={handleWaProvision}
                  disabled={waFormLoading || waEmbeddedLoading}
                  data-testid="wa-provision-submit-btn"
                >
                  {waFormLoading
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : t('centers.whatsapp.provision.submit')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {editingCenter ? t('common.edit') : t('centers.addCenter')}
            </DialogTitle>
	            <DialogDescription className="sr-only">
	              {t('centers.manageCenters') || 'Manage centers'}
	            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pb-6">
            {/* Center Details Section */}
            <div className="space-y-3">
              <div className="text-xs font-medium text-zinc-400 uppercase tracking-wide flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {t('centers.centerDetails') || 'Center Details'}
              </div>
              
              <div className="space-y-2">
                <Label>{t('centers.name')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  className="input-tactical"
                  placeholder="My Fitness Center"
                  data-testid="center-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('centers.timezone')}</Label>
                <Input
                  value={formData.timezone}
                  onChange={(e) => setFormData(f => ({ ...f, timezone: e.target.value }))}
                  className="input-tactical"
                  data-testid="center-timezone-input"
                />
              </div>

            </div>

            {/* Centro sin cobro (demo/cortesía) — solo al crear */}
            {!editingCenter && (
              <div className="space-y-2 border-t border-zinc-800 pt-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.noBilling}
                    onChange={(e) => setFormData(f => ({ ...f, noBilling: e.target.checked }))}
                    data-testid="no-billing-checkbox"
                  />
                  {t('centers.noBilling')}
                </label>
                <p className="text-xs text-zinc-500">{t('centers.noBillingHelp')}</p>
              </div>
            )}

            {/* Admin Section - Only for new centers */}
            {!editingCenter && (
              <div className="space-y-3 border-t border-zinc-800 pt-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium text-zinc-400 uppercase tracking-wide flex items-center gap-2">
                    <UserCog className="w-4 h-4" />
                    {t('centers.centerAdmin') || 'Center Admin'}
                  </div>
                  <Switch
                    checked={createAdmin}
                    onCheckedChange={setCreateAdmin}
                    data-testid="create-admin-switch"
                  />
                </div>
                
                {createAdmin && (
                  <div className="space-y-3 p-3 bg-zinc-900/50 rounded-sm">
                    <div className="space-y-2">
                      <Label>{t('centers.adminName') || 'Admin Name'}</Label>
                      <Input
                        value={formData.adminName}
                        onChange={(e) => setFormData(f => ({ ...f, adminName: e.target.value }))}
                        className="input-tactical"
                        placeholder="John Doe"
                        data-testid="admin-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {t('auth.email')} *
                      </Label>
                      <Input
                        type="email"
                        value={formData.adminEmail}
                        onChange={(e) => setFormData(f => ({ ...f, adminEmail: e.target.value }))}
                        className="input-tactical"
                        placeholder="admin@center.com"
                        data-testid="admin-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        {t('auth.password')} *
                      </Label>
                      <Input
                        type="password"
                        value={formData.adminPassword}
                        onChange={(e) => setFormData(f => ({ ...f, adminPassword: e.target.value }))}
                        className="input-tactical"
                        placeholder="Min 6 characters"
                        data-testid="admin-password-input"
                      />
                    </div>
                    
                    <p className="text-xs text-zinc-500">
                      {t('centers.adminNote') || 'This admin will only have access to this center.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Show existing admin for editing */}
            {editingCenter && centerAdmins[editingCenter.id] && (
              <div className="space-y-2 border-t border-zinc-800 pt-4">
                <div className="text-xs font-medium text-zinc-400 uppercase tracking-wide flex items-center gap-2">
                  <UserCog className="w-4 h-4" />
                  {t('centers.centerAdmin') || 'Center Admin'}
                </div>
                <div className="p-3 bg-zinc-900/50 rounded-sm">
                  <div className="text-sm font-medium">{centerAdmins[editingCenter.id].fullName}</div>
                  <div className="text-xs text-zinc-500">{centerAdmins[editingCenter.id].email}</div>
                </div>
              
                {isSuperAdmin && (
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      onClick={() => handleResetCenterAdminPassword(centerAdmins[editingCenter.id])}
                      data-testid="reset-center-admin-password"
                    >
                      {t('centers.resetPassword')}
                    </Button>
                  </div>
                )}
</div>
            )}

  
            {isSuperAdmin && (
              <div className="space-y-4 p-3 border border-zinc-800 rounded-sm">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4 text-zinc-400" />
                  {t('centers.fichaClientTitle')}
                </div>

                <div className="space-y-2">
                  <Label>{t('centers.fichaLabel')}</Label>

                  {(() => {
                    const enabledSections = normalizeFichaSections(formData.clientFichaSections);
                    const orderedSectionKeys = buildFichaSectionOrder(
                      formData.clientFichaOrder,
                      enabledSections
                    );

                    const orderedOptions = orderedSectionKeys.filter(Boolean);

                    const toggle = (key, checked) => {
                      setFormData((current) => {
                        const enabled = normalizeFichaSections(current.clientFichaSections);
                        const order = buildFichaSectionOrder(current.clientFichaOrder, enabled);

                        const nextEnabled = checked
                          ? order.filter((sectionKey) => enabled.includes(sectionKey) || sectionKey === key)
                          : enabled.filter((sectionKey) => sectionKey !== key);

                        return {
                          ...current,
                          clientFichaSections: nextEnabled,
                          clientFichaOrder: order,
                        };
                      });
                    };

                    const move = (key, direction) => {
                      setFormData((current) => {
                        const enabled = normalizeFichaSections(current.clientFichaSections);
                        const order = buildFichaSectionOrder(current.clientFichaOrder, enabled);
                        const nextOrder = moveFichaSection(order, key, direction);

                        return {
                          ...current,
                          clientFichaOrder: nextOrder,
                          clientFichaSections: nextOrder.filter((sectionKey) => enabled.includes(sectionKey)),
                        };
                      });
                    };

                    return (
                      <div className="space-y-2">
                        {orderedOptions.map((sectionKey, index) => {
                          const enabled = enabledSections.includes(sectionKey);
                          const isFirst = index === 0;
                          const isLast = index === orderedOptions.length - 1;

                          return (
                            <div key={sectionKey} className="flex items-center justify-between gap-3">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={enabled}
                                  onChange={(e) => toggle(sectionKey, e.target.checked)}
                                />
                                {t(`clientProfile.sections.${sectionKey}`)}
                              </label>

                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  className="p-1 rounded-sm border border-zinc-800 disabled:opacity-40"
                                  disabled={!enabled || isFirst}
                                  onClick={() => move(sectionKey, 'up')}
                                  title={t('centers.moveUp')}
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>

                                <button
                                  type="button"
                                  className="p-1 rounded-sm border border-zinc-800 disabled:opacity-40"
                                  disabled={!enabled || isLast}
                                  onClick={() => move(sectionKey, 'down')}
                                  title={t('centers.moveDown')}
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}

                        <div className="text-xs text-zinc-400">
                          {t('centers.fichaTip')}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className="space-y-2">
                  <Label>{t('centers.rgpdLegalText')}</Label>
                  <Textarea
                    value={formData.rgpdLegalText || ''}
                    onChange={(e) => setFormData(f => ({ ...f, rgpdLegalText: e.target.value }))}
                    className="input-tactical min-h-[120px]"
                  />
                </div>

                {/* Credenciales WhatsApp: solo desde la pestaña "WhatsApp" (Provisión de WhatsApp),
                    no en el alta/edición del centro (guard T-46 en POST/PUT /centers). */}
                <div className="space-y-2 border-t border-zinc-800 pt-4">
                  <div className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
                    {t('centers.whatsappTitle')}
                  </div>
                  <p className="text-xs text-zinc-500">{t('centers.whatsappManagedElsewhere')}</p>
                </div>
              </div>
            )}
          <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleSave}
                disabled={formLoading}
                data-testid="save-center-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp reminder credits modal */}
      <Dialog open={showWaCreditsModal} onOpenChange={setShowWaCreditsModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Créditos recordatorios WhatsApp
            </DialogTitle>
            <DialogDescription className="sr-only">
              Añadir créditos de mensajes de recordatorio de pago
            </DialogDescription>
          </DialogHeader>
          {waCreditsCenter && (
            <p className="text-sm text-zinc-400 -mt-2 mb-1 font-medium">{waCreditsCenter.name}</p>
          )}
          {waCreditsCenter && waCreditsCenter.whatsappReminderCredits !== null &&
            waCreditsCenter.whatsappReminderCredits !== undefined && (
            <p className="text-xs text-zinc-500 mb-4">
              Créditos actuales: <span className={`font-semibold ${
                waCreditsCenter.whatsappReminderCredits <= 0 ? 'text-red-400' :
                waCreditsCenter.whatsappReminderCredits <= 20 ? 'text-yellow-400' : 'text-zinc-300'
              }`}>{waCreditsCenter.whatsappReminderCredits}</span>
            </p>
          )}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Créditos a añadir</Label>
              <Input
                type="number"
                min="1"
                value={waCreditsValue}
                onChange={e => setWaCreditsValue(e.target.value)}
                placeholder="100"
                className="input-tactical"
                data-testid="add-wa-credits-input"
              />
              <p className="text-xs text-zinc-500">
                Cada crédito corresponde a un mensaje de recordatorio de pago (Meta).
                Los mensajes gratuitos (bot) no consumen créditos.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowWaCreditsModal(false)} disabled={waCreditsLoading}>
                Cancelar
              </Button>
              <Button
                className="btn-primary"
                onClick={handleAddWaCredits}
                disabled={waCreditsLoading || !waCreditsValue}
                data-testid="confirm-add-wa-credits-btn"
              >
                {waCreditsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Secretary — Añadir minutos modal */}
      <Dialog open={showAddMinutesModal} onOpenChange={setShowAddMinutesModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2">
              <Bot className="w-4 h-4" />
              Añadir minutos — Secretaria IA
            </DialogTitle>
            <DialogDescription className="sr-only">
              Añadir minutos de voz al plan del centro
            </DialogDescription>
          </DialogHeader>
          {addMinutesCenter && (
            <p className="text-sm text-zinc-400 -mt-2 mb-4 font-medium">{addMinutesCenter.name}</p>
          )}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Minutos a añadir</Label>
              <Input
                type="number"
                min="1"
                value={addMinutesValue}
                onChange={e => setAddMinutesValue(e.target.value)}
                placeholder="60"
                className="input-tactical"
                data-testid="add-minutes-input"
              />
              {addMinutesCenter && aiSecStats[addMinutesCenter.id] && (
                <p className="text-xs text-zinc-500">
                  Plan actual: {aiSecStats[addMinutesCenter.id]?.voiceMinutesIncluded || 60} min incluidos
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddMinutesModal(false)} disabled={addMinutesLoading}>
                Cancelar
              </Button>
              <Button
                className="btn-primary"
                onClick={handleAddMinutes}
                disabled={addMinutesLoading || !addMinutesValue}
                data-testid="confirm-add-minutes-btn"
              >
                {addMinutesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offboard — baja completa (irreversible) */}
      <Dialog
        open={showOffboardDialog}
        onOpenChange={(open) => { if (!offboardLoading) setShowOffboardDialog(open); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide flex items-center gap-2 text-red-400">
              <Trash2 className="w-4 h-4" />
              Eliminar centro permanentemente
            </DialogTitle>
            <DialogDescription className="sr-only">
              Baja completa e irreversible del centro y de todos sus datos
            </DialogDescription>
          </DialogHeader>
          {offboardCenter && (
            <div className="space-y-4">
              <div className="rounded-sm border border-red-500/30 bg-red-500/5 p-3 space-y-2">
                <p className="text-sm text-zinc-200">
                  Vas a eliminar <span className="font-semibold text-red-400">{offboardCenter.name}</span> de forma{' '}
                  <span className="font-semibold">irreversible</span>.
                </p>
                <p className="text-xs text-zinc-400">
                  Se borrarán TODOS los datos del centro: clientes, reservas, bonos, planes,
                  conversaciones y mensajes de WhatsApp, notificaciones, registros de auditoría,
                  suscripción, usuarios y métodos de pago asociados. Esta acción no se puede deshacer.
                </p>
                <p className="text-xs text-zinc-500">
                  Si el centro tiene facturas emitidas, la operación se bloqueará por obligación
                  fiscal (conservación 5 años). En ese caso, desactívalo en lugar de eliminarlo.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">
                  Escribe <span className="font-semibold text-zinc-200">{offboardCenter.name}</span> para confirmar
                </Label>
                <Input
                  value={offboardConfirmText}
                  onChange={e => setOffboardConfirmText(e.target.value)}
                  placeholder={offboardCenter.name}
                  className="input-tactical"
                  autoComplete="off"
                  data-testid="offboard-confirm-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowOffboardDialog(false)} disabled={offboardLoading}>
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleOffboard}
                  disabled={offboardLoading || offboardConfirmText !== offboardCenter.name}
                  data-testid="confirm-offboard-btn"
                >
                  {offboardLoading
                    ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    : <Trash2 className="w-4 h-4 mr-2" />}
                  Confirmar eliminación
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export { buildCenterWebsiteDraft, validateCenterWebsiteDraft, formatCenterWebsiteError, normalizeOptionalWebsiteUrls, deriveWebsiteSlug };
